/**
 * Train Simulator HTML HUD overlay.
 *
 * The HUD must live INSIDE the 1920×1080 canvas surface — not the
 * raw viewport — because the canvas itself is scaled/letter-boxed by
 * `Game._applyFit()`. We do that by mounting a DOM "design surface"
 * that mirrors the canvas's `position:absolute; top:50%; left:50%;
 * transform: translate(-50%,-50%) scale(fit)` setup. From that point
 * on every child positions itself in design pixels (1920×1080) and
 * scales together with the game.
 *
 * Layout (mirrors the reference Menetrend mockup):
 *
 *   ┌──────────── TOP CENTRE: MENETREND ────────────┐
 *   │ Schedule table — Time / From / To / Train /   │
 *   │ Status — one row per train, live status chip. │
 *   └───────────────────────────────────────────────┘
 *
 *   ┌─ VÁLTÓ ──┐  ┌─ LÁMPA ───┐                     ┌─ LEGEND ──┐
 *   │ btn btn  │  │  btn btn  │                     │ chip name │
 *   │ btn btn  │  │  btn btn  │                     │ chip name │
 *   └──────────┘  └───────────┘                     └───────────┘
 *
 * Both rows are generated ONE-PER-SWITCH: the VÁLTÓ tile cycles a
 * switch's routing (`board.toggleSwitch`), the LÁMPA tile toggles
 * that switch's stop/go signal (`board.toggleSwitchSignal`). The
 * path-prediction overlay re-routes immediately on either. (Level-
 * crossing barriers are automatic + car-only now, so they get no
 * tiles.)
 */

import { t, otherLang } from '../../config/i18n.js';

const DESIGN_W = 1920;
const DESIGN_H = 1080;

let _stylesInjected = false;
function injectStyles() {
    if (_stylesInjected) return;
    _stylesInjected = true;
    const css = `
    .tshud-surface {
        position:absolute; top:50%; left:50%;
        width:${DESIGN_W}px; height:${DESIGN_H}px;
        transform:translate(-50%,-50%) scale(var(--tshud-scale,1));
        transform-origin:center center;
        pointer-events:none; z-index:120;
        font-family:'Poppins','Inter','Segoe UI',Arial,sans-serif;
        color:#ffffff;
    }
    .tshud-surface * { box-sizing:border-box; }

    /* ── Post-FX classes (applied via _syncFilterClasses) ───
       When the player toggles a shared filter on, we tag the
       HUD root with one of these classes so the DOM widgets
       (clock, schedule, switches, lamps, …) get an effect
       that VISUALLY matches the Pixi shader running on the
       canvas underneath. Pure CSS — no extra DOM, no extra
       paint cost when the class is absent.

       IMPORTANT: the scanline / vignette overlays are scoped
       to INDIVIDUAL HUD WIDGETS (clock card, schedule strip,
       top-right round-button cluster, bottom row), NOT to the
       full .tshud-surface container — .tshud-surface is a
       1920×1080 div that covers the entire canvas area too,
       so a viewport-wide ::before overlay would draw a SECOND
       layer of scanlines on top of the canvas-side CRT. That
       second layer doesn't share the canvas CRT's screen-space
       period (it's anchored to design pixels, the canvas one
       to framebuffer pixels), so the two stack into a dense
       moiré that's the harsh look the user reported. By
       scoping to widget rects we get the same scanline density
       on the DOM as on the canvas, with no overlap or
       double-stamping.
       ============================================== */
    /* CRT — scanlines + slight saturation boost on DOM widgets only.
       Period (~8.8 design pixels) matches the canvas CRT shader at
       its default lineWidth=1.4 (sin(uv.y * uInputSize.y / lineWidth)
       → 2π × 1.4 ≈ 8.8 source pixels per cycle), so the eye reads
       the entire screen as one continuous CRT tube. */
    .tshud-surface.tshud-crt-on {
        filter:contrast(1.06) saturate(1.18);
    }
    /* NOTE: we DO NOT set position:relative on the HUD widgets here
       (clock card, schedule strip, top-right buttons, bottom row).
       They are already position:absolute in their own rules with
       pinned top/left/right coords, so forcing them to relative
       drops them into document flow and stacks them all at the top
       of the surface, breaking the entire layout. An ::after pseudo
       containing block already prefers the nearest positioned
       ancestor, and absolute qualifies — so the scanline overlay
       sits flush over each widget without changing the host element
       position scheme. */
    .tshud-surface.tshud-crt-on .tshud-clock-card::after,
    .tshud-surface.tshud-crt-on .tshud-tr-btn::after,
    .tshud-surface.tshud-crt-on .tshud-schedule::after,
    .tshud-surface.tshud-crt-on .tshud-bottom-row::after {
        content:''; position:absolute; inset:0;
        pointer-events:none; z-index:9999;
        border-radius:inherit;
        background:repeating-linear-gradient(
            to bottom,
            rgba(0,0,0,0.18) 0px,
            rgba(0,0,0,0.0)  4.4px,
            rgba(0,0,0,0.18) 8.8px
        );
        mix-blend-mode:multiply;
    }
    /* Greyscale — single CSS filter, no overlays needed. */
    .tshud-surface.tshud-greyscale-on { filter:grayscale(1); }
    /* Old Film — sepia + slight contrast tilt + grain overlay,
       scoped to widget rects so the canvas isn't double-tinted by
       the same effect that's already running in the Pixi shader. */
    .tshud-surface.tshud-oldfilm-on {
        filter:sepia(0.85) contrast(1.05) brightness(0.95);
    }
    .tshud-surface.tshud-oldfilm-on .tshud-clock-card::after,
    .tshud-surface.tshud-oldfilm-on .tshud-tr-btn::after,
    .tshud-surface.tshud-oldfilm-on .tshud-schedule::after,
    .tshud-surface.tshud-oldfilm-on .tshud-bottom-row::after {
        content:''; position:absolute; inset:0;
        pointer-events:none; z-index:9999;
        border-radius:inherit;
        background:radial-gradient(ellipse at center,
                                   transparent 60%,
                                   rgba(20,10,0,0.30) 100%);
        mix-blend-mode:multiply;
    }
    /* When CRT and Old Film stack, the CRT scanline overlay wins
       (its rule is identical, just with sepia-tinted bands). The
       per-widget ::after collision is avoided because both selectors
       target the same element — last rule defined wins, which is CRT
       above. We pin the sepia tint by setting background-color
       instead of background, so the scanline gradient layered on
       top keeps its period. */
    .tshud-surface.tshud-crt-on.tshud-oldfilm-on .tshud-clock-card::after,
    .tshud-surface.tshud-crt-on.tshud-oldfilm-on .tshud-tr-btn::after,
    .tshud-surface.tshud-crt-on.tshud-oldfilm-on .tshud-schedule::after,
    .tshud-surface.tshud-crt-on.tshud-oldfilm-on .tshud-bottom-row::after {
        background:repeating-linear-gradient(
            to bottom,
            rgba(40,20,0,0.22) 0px,
            rgba(40,20,0,0.0)  4.4px,
            rgba(40,20,0,0.22) 8.8px
        );
    }

    /* =====================================================
       MOCKUP PALETTE (reference screenshot)
         Card body navy : #2f215e   (PONT / ÉLET)
         Schedule navy  : #30215c   (MENETREND wrap + header)
         Golden border  : #f7bd3e   (4px on stat / schedule)
         Switch green   : #358927   (each VÁLTÓ tile)
         Barrier red    : #cc3521   (each SOROMPÓ tile)
         IDŐ orange     : #ee7e1c → #ce5e10 (clock tile)
         Light text     : #ffffff   (titles, numbers)
         Accent gold    : #f7bd3e   (labels, header text)
       ===================================================== */

    /* ── Top-left stat cards (PONT / ÉLET) ──────────── */
    .tshud-stat-stack {
        position:absolute; top:18px; left:22px;
        display:flex; flex-direction:column; gap:10px;
        pointer-events:auto;
        width:200px;
    }
    .tshud-stat-card {
        background:#2f215e;
        /* Sharp corners — per UX direction the entire in-game
           top header (PONT / ÉLET / clock / schedule / back pill)
           runs square so the row reads as one uniform strip. */
        border:4px solid #f7bd3e; border-radius:0;
        padding:10px 16px;
        display:flex; align-items:center; gap:14px;
        box-shadow:0 6px 18px rgba(0,0,0,0.55),
                   inset 0 -3px 0 rgba(0,0,0,0.30);
        color:#ffffff;
        min-height:70px;
    }
    .tshud-stat-icon {
        font-size:42px; line-height:1;
        flex-shrink:0;
        text-shadow:0 2px 0 rgba(0,0,0,0.45);
    }
    .tshud-stat-icon.star  { color:#f7bd3e;
                             text-shadow:0 0 2px #92610a, 0 2px 0 #6b4708; }
    .tshud-stat-icon.heart { color:#ff5c8a;
                             text-shadow:0 2px 0 #7a1d3a; }
    .tshud-stat-body {
        display:flex; flex-direction:column; align-items:flex-start;
        line-height:1.05; flex:1; min-width:0;
    }
    .tshud-stat-label {
        font-size:14px; font-weight:900; letter-spacing:2px;
        color:#ffffff; text-transform:uppercase;
        text-shadow:0 1px 0 rgba(0,0,0,0.45);
    }
    .tshud-stat-value {
        font-size:32px; font-weight:900; letter-spacing:0.5px;
        color:#ffffff;
        text-shadow:0 2px 0 rgba(0,0,0,0.45);
    }
    /* Pip strip kept in DOM but hidden — number alone matches the mockup. */
    .tshud-life-pips { display:none; }
    .tshud-life-pip { display:none; }

    /* ── Top-LEFT clock card (IDŐ) ──────────────────────
       Pinned to the LEFT corner (top:18, left:22) so the
       wall-clock sits where the player's eye lands first.
       The Pixi BACK pill swaps to the top-RIGHT corner via
       SceneChrome's default placement, freeing this corner
       for the clock without anything else fighting for the
       space (PONT / ÉLET cards were removed). */
    .tshud-clock-card {
        position:absolute; top:18px; left:22px;
        background:linear-gradient(180deg,#ee7e1c 0%,#c95e10 100%);
        /* Sharp corners — see .tshud-stat-card note. */
        border:4px solid #f7bd3e; border-radius:0;
        padding:10px 20px;
        display:flex; align-items:center; gap:14px;
        box-shadow:0 6px 18px rgba(0,0,0,0.55),
                   inset 0 -3px 0 rgba(0,0,0,0.30);
        color:#ffffff;
        pointer-events:auto;
        min-height:70px; min-width:200px;
    }
    .tshud-clock-icon {
        font-size:42px; line-height:1; color:#ffffff;
        text-shadow:0 2px 0 rgba(0,0,0,0.45);
    }
    .tshud-clock-body { display:flex; flex-direction:column;
                        line-height:1.05; align-items:flex-start; }
    .tshud-clock-label {
        font-size:14px; font-weight:900; letter-spacing:2px;
        color:#ffffff; text-transform:uppercase;
        text-shadow:0 1px 0 rgba(0,0,0,0.45);
    }
    .tshud-clock-value {
        font-size:32px; font-weight:900; letter-spacing:1px;
        color:#ffffff; font-variant-numeric:tabular-nums;
        text-shadow:0 2px 0 rgba(0,0,0,0.45);
    }

    /* ── Top-right help + leaderboard cluster ───────────
       Sits below the Pixi BACK pill (canvas-space, y=20-90)
       at top:108 so the two stack cleanly on the right
       edge — BACK on top, help/leaderboard underneath. */
    .tshud-tr-buttons {
        position:absolute; top:108px; right:22px;
        display:flex; flex-direction:row-reverse; gap:10px;
        pointer-events:auto;
    }
    .tshud-tr-btn {
        width:56px; height:56px; border-radius:50%;
        border:3px solid #2f215e;
        background:#ffffff;
        color:#2f215e; cursor:pointer;
        display:flex; align-items:center; justify-content:center;
        font-size:26px; font-weight:900; line-height:1;
        box-shadow:0 5px 12px rgba(0,0,0,0.55),
                   inset 0 -3px 0 rgba(0,0,0,0.22);
        transition:transform 0.08s ease, box-shadow 0.1s ease;
    }
    .tshud-tr-btn:hover  { transform:translateY(-1px); }
    .tshud-tr-btn:active { transform:translateY(1px); }
    .tshud-tr-btn.orange {
        background:linear-gradient(180deg,#ee7e1c 0%,#c95e10 100%);
        color:#ffffff; border-color:#f7bd3e;
    }

    /* ── Top schedule strip (MENETREND) ─────────────────
       Wider 1280-px card pinned to the top centre. Trains
       are split across TWO side-by-side mini-tables (like
       real-world station departure boards) so the strip
       stays ~half the height it would be as a single tall
       list. Purple lives ONLY in the title header; the
       tables themselves are white with purple text and a
       yellow column-header strip. */
    .tshud-schedule {
        position:absolute; top:0; left:50%;
        width:1280px; margin-left:-640px;
        background:#ffffff;
        border:4px solid #30215c;
        border-top:0;
        /* Sharp corners — top-header strip is square per UX
           direction. Was 0 0 18px 18px (rounded bottom only). */
        border-radius:0;
        padding:0;
        box-shadow:0 8px 22px rgba(0,0,0,0.6);
        pointer-events:auto;
        overflow:hidden;
        max-height:200px;
        display:flex; flex-direction:column;
    }
    /* Two-column body — left and right tables sit side-by-side
       with a thin purple divider between them. Each table is
       independently scrollable (sticky thead) so 6+ trains
       still fit without stretching the strip taller. */
    .tshud-schedule-columns {
        display:flex; flex-direction:row;
        align-items:stretch;
        background:#ffffff;
        min-height:0;
    }
    .tshud-schedule-col {
        flex:1 1 0; min-width:0;
        display:flex; flex-direction:column;
        background:#ffffff;
    }
    .tshud-schedule-col + .tshud-schedule-col {
        border-left:2px solid #30215c;
    }
    .tshud-schedule-header {
        display:flex; align-items:center; justify-content:space-between;
        gap:10px;
        padding:8px 16px;
        background:#30215c;
        border-bottom:2px solid #30215c;
    }
    .tshud-schedule-title {
        text-align:center; font-size:22px; font-weight:900;
        letter-spacing:6px; color:#ffffff;
        text-shadow:0 2px 0 rgba(0,0,0,0.55);
        flex:1;
    }

    /* Compact toolbar chips that ride on the schedule header.
       The header is now just RESTART (left) and ROUTES (right) —
       SOLVE / SPEED / REWIND / AUTOPILOT all moved off the header
       (REWIND + SPEED to the big bottom-row tiles, SOLVE +
       AUTOPILOT removed entirely). */
    .tshud-routes-toggle, .tshud-restart-btn {
        background:#f7bd3e;
        color:#30215c; border:2px solid #30215c;
        /* Sharp corners — these toolbar chips ride on the schedule
           strip in the top header, so they follow the same square
           treatment as the surrounding cards. */
        border-radius:0;
        padding:3px 9px;
        font-family:inherit; font-weight:900;
        font-size:10px; letter-spacing:1.2px; text-transform:uppercase;
        cursor:pointer;
        display:flex; align-items:center; gap:5px;
        transition:transform 0.08s ease, filter 0.1s ease;
    }
    .tshud-routes-toggle:hover, .tshud-restart-btn:hover { filter:brightness(0.95); }
    .tshud-routes-toggle:active, .tshud-restart-btn:active { transform:translateY(1px); }
    .tshud-routes-toggle.off {
        background:#3b2d6e; color:#cbb6f4; border-color:#1c1240;
    }
    .tshud-routes-toggle .dot {
        width:9px; height:9px; border-radius:50%;
        background:#22c55e; box-shadow:0 0 4px rgba(34,197,94,0.7);
    }
    .tshud-routes-toggle.off .dot { background:#6b5e91; box-shadow:none; }

    /* ── Big action tiles in the centre glass panel ─────────────
       Premium gaming-console look: 3-row stack (icon | label |
       state pill), subtle inset gradient for depth, soft drop
       shadow that reads as a real button you can press. Each
       variant (.pause / .focus / .rewind / .speed) tints the
       same silhouette so they stay visually unified inside the
       shared glass frame above. */
    .tshud-action-tile {
        /* Sharp corners — matches the rest of the bottom-toolbar
           tiles (.tshud-tile) and the surrounding glass panel
           (.tshud-actions-group). DO NOT wrap CSS-class names in
           backticks here — this whole block lives inside a JS
           template literal and a stray backtick terminates the
           string, killing the entire HUD at runtime. */
        border-radius:0;
        padding:7px 10px 9px;
        display:flex; flex-direction:column; align-items:center;
        justify-content:space-between; gap:4px;
        min-width:96px; max-width:118px;
        cursor:pointer; font-family:inherit; font-weight:900;
        color:#ffffff;
        box-shadow:
            0 4px 10px rgba(0,0,0,0.45),
            0 0 0 1px rgba(0,0,0,0.40),
            inset 0 1px 0 rgba(255,255,255,0.22),
            inset 0 -3px 0 rgba(0,0,0,0.22);
        transition:transform 0.10s ease, filter 0.12s ease,
                   box-shadow 0.18s ease;
        border:2px solid transparent;
        user-select:none;
        position:relative;
        overflow:hidden;
    }
    /* Glossy sheen across the upper half of every tile —
       gives the buttons a real "plastic" depth without any
       extra DOM nodes. Sits BELOW the children (z-index 0)
       so the icon / label / pill stack always reads clearly
       on top of it. */
    .tshud-action-tile::before {
        content:''; position:absolute;
        top:0; left:0; right:0; height:55%;
        background:linear-gradient(180deg,
            rgba(255,255,255,0.22) 0%,
            rgba(255,255,255,0.06) 60%,
            transparent 100%);
        pointer-events:none;
        /* Sharp top corners on the gloss sheen too — was a 12px
           radius matching the old rounded tile. */
        border-radius:0;
    }
    .tshud-action-tile > * { position:relative; z-index:1; }
    .tshud-action-tile:hover  {
        transform:translateY(-2px);
        filter:brightness(1.10);
        box-shadow:
            0 7px 16px rgba(0,0,0,0.55),
            0 0 0 1px rgba(0,0,0,0.45),
            inset 0 1px 0 rgba(255,255,255,0.30),
            inset 0 -3px 0 rgba(0,0,0,0.22);
    }
    .tshud-action-tile:active {
        transform:translateY(2px);
        box-shadow:
            0 1px 3px rgba(0,0,0,0.40),
            0 0 0 1px rgba(0,0,0,0.45),
            inset 0 2px 4px rgba(0,0,0,0.30),
            inset 0 -1px 0 rgba(0,0,0,0.10);
    }
    .tshud-action-tile .tshud-action-label {
        font-size:11px; letter-spacing:1.4px;
        text-transform:uppercase;
        color:#ffffff;
        text-shadow:0 1px 0 rgba(0,0,0,0.55);
        line-height:1;
    }
    .tshud-action-tile .tshud-action-icon {
        font-size:30px; line-height:1;
        text-shadow:0 2px 0 rgba(0,0,0,0.55),
                    0 0 14px rgba(255,255,255,0.10);
        filter:drop-shadow(0 1px 1px rgba(0,0,0,0.45));
    }
    .tshud-action-tile .tshud-action-value {
        font-size:13px; font-weight:900; letter-spacing:0.5px;
        background:rgba(0,0,0,0.42);
        color:#ffffff;
        border-radius:999px;
        padding:3px 12px;
        min-width:40px; text-align:center;
        box-shadow:
            inset 0 1px 2px rgba(0,0,0,0.55),
            inset 0 -1px 0 rgba(255,255,255,0.08);
        text-shadow:0 1px 0 rgba(0,0,0,0.55);
    }

    /* REWIND tile — electric cyan to read as "second-chance" / time
       travel. Disabled state greys to spent. */
    .tshud-action-tile.rewind {
        background:#22d3ee; color:#0c4a6e; border-color:#0c4a6e;
    }
    .tshud-action-tile.rewind .tshud-action-label,
    .tshud-action-tile.rewind .tshud-action-icon {
        color:#0c4a6e; text-shadow:none;
    }
    .tshud-action-tile.rewind .tshud-action-value {
        background:#0c4a6e; color:#bef0ff;
    }
    .tshud-action-tile.rewind.disabled,
    .tshud-action-tile.rewind:disabled {
        background:#475569; color:#cbd5e1; border-color:#1e293b;
        cursor:not-allowed; filter:none; transform:none;
    }
    .tshud-action-tile.rewind.disabled .tshud-action-label,
    .tshud-action-tile.rewind.disabled .tshud-action-icon,
    .tshud-action-tile.rewind:disabled .tshud-action-label,
    .tshud-action-tile.rewind:disabled .tshud-action-icon {
        color:#cbd5e1;
    }
    .tshud-action-tile.rewind.disabled .tshud-action-value,
    .tshud-action-tile.rewind:disabled .tshud-action-value {
        background:#1e293b; color:#94a3b8;
    }

    /* PAUSE tile — emerald green at rest ("press to pause"), flips to
       a saturated amber-red "PAUSED" badge with a slow breathing
       ring while engaged so the player can't miss the world is on
       hold. The .paused class is toggled by _syncPauseTile every
       frame; absence of it = play state. */
    .tshud-action-tile.pause {
        background:#10b981; color:#064e3b; border-color:#064e3b;
    }
    .tshud-action-tile.pause .tshud-action-label,
    .tshud-action-tile.pause .tshud-action-icon {
        color:#064e3b; text-shadow:none;
    }
    .tshud-action-tile.pause .tshud-action-value {
        background:#064e3b; color:#bbf7d0;
    }
    .tshud-action-tile.pause.paused {
        background:#f59e0b; color:#78350f; border-color:#78350f;
        box-shadow:0 0 14px rgba(245,158,11,0.55),
                   0 3px 8px rgba(0,0,0,0.5),
                   inset 0 -3px 0 rgba(0,0,0,0.22);
        animation:tshud-pause-pulse 1.4s ease-in-out infinite;
    }
    .tshud-action-tile.pause.paused .tshud-action-label,
    .tshud-action-tile.pause.paused .tshud-action-icon {
        color:#78350f; text-shadow:none;
    }
    .tshud-action-tile.pause.paused .tshud-action-value {
        background:#78350f; color:#fde68a;
    }
    @keyframes tshud-pause-pulse {
        0%,100% { filter:brightness(1.00); }
        50%     { filter:brightness(1.15); }
    }

    /* FOCUS tile — deep purple at rest ("press to focus"), flips to
       a glowing magenta "FOCUS ON" while engaged. The class swap
       (.on) is driven by _syncFocusTile. */
    .tshud-action-tile.focus {
        background:#7c3aed; color:#f5d0fe; border-color:#4c1d95;
    }
    .tshud-action-tile.focus .tshud-action-label,
    .tshud-action-tile.focus .tshud-action-icon {
        color:#f5d0fe; text-shadow:0 2px 0 rgba(0,0,0,0.55);
    }
    .tshud-action-tile.focus .tshud-action-value {
        background:#4c1d95; color:#f5d0fe;
    }
    .tshud-action-tile.focus.on {
        background:#a21caf; color:#ffffff; border-color:#4a044e;
        box-shadow:0 0 14px rgba(217,70,239,0.65),
                   0 3px 8px rgba(0,0,0,0.5),
                   inset 0 -3px 0 rgba(0,0,0,0.22);
    }
    .tshud-action-tile.focus.on .tshud-action-label,
    .tshud-action-tile.focus.on .tshud-action-icon {
        color:#ffffff;
    }
    .tshud-action-tile.focus.on .tshud-action-value {
        background:#4a044e; color:#f5d0fe;
    }

    /* SPEED tile — yellow at 1×, warm orange at 2×, red glow at 4×,
       so the player gets a temperature cue for the train tempo. */
    .tshud-action-tile.speed {
        background:#f7bd3e; color:#30215c; border-color:#30215c;
    }
    .tshud-action-tile.speed .tshud-action-label,
    .tshud-action-tile.speed .tshud-action-icon {
        color:#30215c; text-shadow:none;
    }
    .tshud-action-tile.speed .tshud-action-value {
        background:#30215c; color:#fde68a;
    }
    .tshud-action-tile.speed.s2 {
        background:#fb923c; color:#7c2d12; border-color:#7c2d12;
    }
    .tshud-action-tile.speed.s2 .tshud-action-label,
    .tshud-action-tile.speed.s2 .tshud-action-icon { color:#7c2d12; }
    .tshud-action-tile.speed.s2 .tshud-action-value {
        background:#7c2d12; color:#fed7aa;
    }
    .tshud-action-tile.speed.sN {
        background:#cc3521; color:#ffffff; border-color:#7f1d1d;
        box-shadow:0 0 12px rgba(239,68,68,0.6),
                   0 3px 8px rgba(0,0,0,0.5),
                   inset 0 -3px 0 rgba(0,0,0,0.22);
    }
    .tshud-action-tile.speed.sN .tshud-action-label,
    .tshud-action-tile.speed.sN .tshud-action-icon { color:#ffffff; text-shadow:0 2px 0 rgba(0,0,0,0.55); }
    .tshud-action-tile.speed.sN .tshud-action-value {
        background:#7f1d1d; color:#fecaca;
    }

    .tshud-schedule-left  { display:flex; align-items:center; gap:6px; }
    .tshud-schedule-right { display:flex; align-items:center; gap:6px; }

    .tshud-toast {
        position:absolute; top:210px; left:50%;
        transform:translateX(-50%);
        background:#30215c; color:#ffffff;
        border:3px solid #f7bd3e; border-radius:10px;
        padding:10px 22px; font-size:14px; font-weight:900;
        letter-spacing:1px; text-transform:uppercase;
        box-shadow:0 6px 18px rgba(0,0,0,0.6);
        pointer-events:none;
        animation:tshud-toast-in 0.25s ease;
    }
    .tshud-toast.error   { border-color:#cc3521; color:#fca5a5; max-width:80vw; text-align:center; }
    .tshud-toast.warn    { border-color:#f59e0b; color:#fde68a; max-width:80vw; text-align:center; }
    .tshud-toast.success { border-color:#358927; color:#bbf7d0; }
    @keyframes tshud-toast-in {
        from { opacity:0; transform:translate(-50%, -12px); }
        to   { opacity:1; transform:translateX(-50%); }
    }

    /* Centred "dangerous traffic situation" banner. Replaces the
       game-over modal for collisions per client feedback — sits
       prominently in the middle of the screen for the duration of
       the auto-rewind and goes away once the trains are settled
       back to a safe ~10s-old state. */
    .tshud-danger-banner {
        position:absolute;
        top:50%; left:50%;
        transform:translate(-50%, -50%);
        background:#7f1d1d; color:#ffffff;
        border:4px solid #fbbf24; border-radius:14px;
        padding:22px 38px;
        font-size:20px; font-weight:900;
        letter-spacing:0.5px; line-height:1.35;
        text-align:center;
        max-width:80vw;
        box-shadow:0 12px 30px rgba(0,0,0,0.65),
                   0 0 0 4px rgba(251,191,36,0.25);
        pointer-events:none;
        z-index:50;
        animation:tshud-danger-in 0.32s ease-out;
    }
    @keyframes tshud-danger-in {
        from { opacity:0; transform:translate(-50%, -50%) scale(0.85); }
        to   { opacity:1; transform:translate(-50%, -50%) scale(1.00); }
    }

    .tshud-schedule-table {
        width:100%; border-collapse:collapse; font-size:12px;
        background:#ffffff; table-layout:fixed;
    }
    .tshud-schedule-table th {
        text-align:center; font-size:10px; font-weight:900;
        letter-spacing:1px; color:#30215c;
        padding:5px 4px;
        background:#f7bd3e;
        border-bottom:2px solid #30215c;
        text-transform:uppercase;
        position:sticky; top:0; z-index:2;
        white-space:nowrap;
    }
    .tshud-schedule-table td {
        padding:4px 4px; color:#30215c; vertical-align:middle;
        font-size:12px; font-weight:700; text-align:center;
        background:#ffffff;
        overflow:hidden; text-overflow:ellipsis;
    }
    .tshud-schedule-table td.tshud-time { font-weight:900; color:#30215c; }
    .tshud-schedule-table td.tshud-platform {
        font-weight:900; color:#30215c; font-size:14px;
    }
    .tshud-schedule-table tbody tr + tr td {
        border-top:1px solid rgba(48,33,92,0.12);
    }
    .tshud-schedule-table tbody tr:nth-child(even) td {
        background:#f7f4ff;
    }
    .tshud-schedule-table tr.idle    td { opacity:0.85; }
    .tshud-schedule-table tr.running td { color:#30215c; font-weight:800; }
    .tshud-schedule-table tr.arrived td { color:#15803d; }
    .tshud-schedule-table tr.crashed td { color:#cc3521; text-decoration:line-through; }
    /* Column-width hints (sum to ~100%) so the 6 cells lay
       out predictably across each half-table without one
       overflowing into another. TIME and TRACK stay narrow;
       FROM and TO get the most room (longest station names);
       TRAIN/STATUS chips are mid-width. */
    .tshud-schedule-table col.tshud-w-time     { width:11%; }
    .tshud-schedule-table col.tshud-w-from     { width:21%; }
    .tshud-schedule-table col.tshud-w-to       { width:21%; }
    .tshud-schedule-table col.tshud-w-train    { width:17%; }
    .tshud-schedule-table col.tshud-w-platform { width:9%;  }
    .tshud-schedule-table col.tshud-w-status   { width:21%; }
    .tshud-schedule-body-wrap {
        max-height:148px; background:#ffffff;
        flex:1 1 auto; min-height:0;
        /* Vertical scroll only. overflow-x MUST be hidden: the
           next-up ÁLLAPOT cell pulses via transform:scale(1.14)
           which momentarily overflows the cell horizontally. With
           overflow-y:auto the spec promotes overflow-x to auto too,
           so that overflow popped a HORIZONTAL scrollbar whose
           purple (#30215c) thumb grew/shrank in time with the
           pulse — read by the player as a "pulsating purple bar"
           at the bottom of the table. Clipping x kills it. */
        overflow-x:hidden; overflow-y:auto;
    }
    .tshud-schedule-body-wrap::-webkit-scrollbar { width:6px; }
    .tshud-schedule-body-wrap::-webkit-scrollbar-thumb {
        background:#30215c; border-radius:3px;
    }

    /* VONAT column — small horizontal "train" pill. Sized
       to fit comfortably inside the half-width tables. */
    .tshud-train-chip {
        display:inline-flex; align-items:center; justify-content:center;
        vertical-align:middle;
        /* Sharp corners — these mini train pills live inside the
           top-header schedule, so they match the square-corner
           direction applied to the rest of the strip. */
        padding:2px 6px; border-radius:0;
        font-weight:900; font-size:10px; letter-spacing:0.3px;
        color:#fff; min-width:44px; max-width:100%; text-align:center;
        border:2px solid rgba(0,0,0,0.55);
        box-shadow:inset 0 -3px 0 rgba(0,0,0,0.28),
                   inset 0 1px 0 rgba(255,255,255,0.18);
        white-space:nowrap; overflow:hidden; text-overflow:ellipsis;
    }
    .tshud-station-name {
        font-weight:700; font-size:12px; color:#30215c;
        white-space:nowrap; overflow:hidden; text-overflow:ellipsis;
        display:inline-block; max-width:100%;
    }

    .tshud-status {
        display:inline-block;
        font-size:11px; font-weight:900; letter-spacing:0.8px;
        text-transform:uppercase;
        padding:1px 4px;
        white-space:nowrap;
    }
    .tshud-status.waiting  { color:#7c6da3; }
    .tshud-status.next     { color:#7c6da3; }
    .tshud-status.departing{ color:#15803d; }
    .tshud-status.running  { color:#15803d; }
    .tshud-status.arrived  { color:#15803d; }
    .tshud-status.crashed  { color:#cc3521; }
    /* Live countdown to a not-yet-departed train, rendered in grey
       right after its KÖVETKEZŐ / INDUL status. Tabular numerals so
       the digits don't jitter as the clock ticks down. */
    .tshud-countdown {
        display:inline-block;
        margin-left:5px;
        font-size:11px; font-weight:800; letter-spacing:0.5px;
        color:#9a8fb8;
        font-variant-numeric:tabular-nums;
    }
    /* BOARDING — the train enters its final 10-second pre-departure
       window. The status pill flashes green-on-white and the whole
       row pulses with a faint amber tint so the player can see at
       a glance which line is "next out". Animation is deliberately
       short-cycle (0.7 s) so it reads as URGENT but won't trigger
       motion-sickness on long stages. */
    .tshud-status.boarding {
        color:#f8fafc;
        background:#15803d;
        padding:2px 6px;
        border-radius:3px;
        animation: tshud-boarding-pill 0.7s ease-in-out infinite alternate;
    }
    @keyframes tshud-boarding-pill {
        0%   { background:#15803d; box-shadow:0 0 0 0 rgba(34,197,94,0.0); }
        100% { background:#22c55e; box-shadow:0 0 0 4px rgba(34,197,94,0.45); }
    }
    .tshud-schedule-table tr.boarding {
        animation: tshud-boarding-row 0.7s ease-in-out infinite alternate;
    }
    @keyframes tshud-boarding-row {
        0%   { background:rgba(251,191,36,0.00); }
        100% { background:rgba(251,191,36,0.20); }
    }
    /* NEXT DEPARTURE — the single train that will start FIRST (the
       waiting line with the least time left). Its ÁLLAPOT cell — the
       KÖVETKEZŐ M:SS time readout — gently GROWS and shrinks so the
       player notices which train is up next. Pulse is SCALE-ONLY: no
       background bar, no colour/opacity change, so the text + time
       keep their normal dark colour and stay fully readable. */
    .tshud-schedule-table td.tshud-state.next-up {
        transform-origin:center center;
        animation: tshud-next-up 0.85s ease-in-out infinite alternate;
    }
    @keyframes tshud-next-up {
        0%   { transform:scale(1); }
        100% { transform:scale(1.14); }
    }

    .tshud-reach {
        display:inline-flex; align-items:center; gap:4px;
        /* Sharp corners — outcome pill rides in the top-header
           schedule's STATUS column, follows the same square
           treatment as the rest of the strip. */
        padding:1px 7px; border-radius:0;
        font-size:10px; font-weight:900; letter-spacing:1px;
        text-transform:uppercase;
    }
    .tshud-reach.ok    { background:rgba(134,239,172,0.18); color:#86efac; border:1px solid #86efac; }
    .tshud-reach.crash { background:rgba(252,165,165,0.18); color:#fca5a5; border:1px solid #fca5a5; }
    .tshud-reach.loop  { background:rgba(252,211,77,0.18); color:#fde68a; border:1px solid #fde68a; }
    .tshud-reach-icon  { font-size:11px; line-height:1; }

    /* ── Bottom control bar ─────────────────────────────
       Three-zone strip: switches gutter (left), glass-panelled
       action group (centre), lamps gutter (right). The side
       gutters flex to fill available width while the centre
       panel locks to its content — keeps the player's primary
       controls anchored to the visual centre even when the
       switch / lamp count varies between stages. */
    .tshud-bottom-row {
        position:absolute; left:18px; right:18px; bottom:18px;
        display:flex; align-items:stretch; justify-content:space-between;
        gap:14px;
        pointer-events:auto;
        max-height:104px;
    }
    .tshud-side-group {
        display:flex; align-items:stretch; gap:8px;
        flex:1 1 0; min-width:0;
    }
    .tshud-side-left  { justify-content:flex-start; }
    .tshud-side-right { justify-content:flex-end; }

    /* ── Action group glass panel ───────────────────────
       Frames PAUSE / FOCUS / REWIND / SPEED in a single
       translucent console so they read as ONE control
       surface (the player's global cockpit) instead of
       four loose tiles competing with the switches and
       lamps. The blur + warm gold border match the
       schedule strip up top so the HUD reads as a
       coherent set of panels. */
    .tshud-actions-group {
        display:flex; align-items:stretch; gap:10px;
        padding:8px 12px;
        background:linear-gradient(180deg,
            rgba(15,5,40,0.78) 0%,
            rgba(8,3,28,0.92) 100%);
        -webkit-backdrop-filter:blur(10px);
        backdrop-filter:blur(10px);
        /* Sharp corners — UX direction is to drop ALL rounded corners on
           the bottom toolbar (switches, action panel, lamps) so the
           in-game footer reads as one flat console strip, matching the
           sharp top header. */
        border-radius:0;
        border:2px solid rgba(247,189,62,0.55);
        box-shadow:
            0 10px 28px rgba(0,0,0,0.55),
            0 0 0 1px rgba(0,0,0,0.45),
            inset 0 1px 0 rgba(255,255,255,0.10),
            inset 0 -1px 0 rgba(0,0,0,0.50);
        flex-shrink:0;
        position:relative;
    }
    /* Subtle "control panel" top-edge highlight strip. */
    .tshud-actions-group::before {
        content:''; position:absolute;
        top:-1px; left:14px; right:14px; height:2px;
        background:linear-gradient(90deg,
            transparent 0%,
            rgba(247,189,62,0.55) 18%,
            rgba(255,255,255,0.85) 50%,
            rgba(247,189,62,0.55) 82%,
            transparent 100%);
        border-radius:2px;
        pointer-events:none;
    }

    /* Each switch / lamp is its OWN coloured tile — no group
       wrapper, matches the mockup exactly. Sharp corners + a
       larger top inset so the "VÁLTÓ N" / "SOROMPÓ N" label
       has breathing room above it (the 2px label line is
       short, so without extra top padding it sat right
       against the tile's top border). */
    .tshud-tile {
        border-radius:0; padding:10px 6px 5px;
        display:flex; flex-direction:column; align-items:center;
        justify-content:space-between; gap:2px;
        flex:1; min-width:0;
        cursor:pointer; font-family:inherit; font-weight:900;
        color:#ffffff;
        box-shadow:0 3px 8px rgba(0,0,0,0.5),
                   inset 0 -3px 0 rgba(0,0,0,0.22);
        transition:transform 0.08s ease, filter 0.1s ease;
        border:2px solid transparent;
    }
    .tshud-tile:hover  { transform:translateY(-1px); filter:brightness(1.05); }
    .tshud-tile:active { transform:translateY(1px); }
    /* Switches get the schedule-table purple so every CONTROL on
       the toolbar reads as "tap me to change routing" instead of
       fighting the green/red lamp signals. */
    .tshud-tile.switch {
        background:#5b3a9c; border-color:#30215c;
        max-width:108px;
    }
    /* Brief "you toggled this" flash on the purple switch tile —
       brightens the background to a hot magenta wash and pulses
       the border for ~350 ms so the player can see WHICH tile
       just changed, both when they tap the tile itself AND when
       they tap the matching on-board switch. The class is removed
       on the animationend event so the next toggle re-triggers
       cleanly. */
    .tshud-tile.switch.flash {
        animation:tshud-switch-flash 0.35s ease-out;
    }
    @keyframes tshud-switch-flash {
        0%   { background:#ff2dd6; border-color:#ffffff;
               box-shadow:0 0 0 0 rgba(255,45,214,0.85),
                          0 3px 8px rgba(0,0,0,0.5),
                          inset 0 -3px 0 rgba(0,0,0,0.22); }
        60%  { background:#a04ec9; border-color:#ff2dd6;
               box-shadow:0 0 0 6px rgba(255,45,214,0.0),
                          0 3px 8px rgba(0,0,0,0.5),
                          inset 0 -3px 0 rgba(0,0,0,0.22); }
        100% { background:#5b3a9c; border-color:#30215c;
               box-shadow:0 3px 8px rgba(0,0,0,0.5),
                          inset 0 -3px 0 rgba(0,0,0,0.22); }
    }
    /* Lamps mirror their CURRENT signal state — red tile when the
       barrier is closed (Állj!), green tile when it's open
       (Szabad). The data attribute is poked by _syncLampBtn /
       refresh() whenever the entity flips, so the background
       follows the on-board barrier in real time. A neutral
       fallback covers the "not yet synced" first frame. */
    .tshud-tile.lamp {
        background:#cc3521; border-color:#7f1d1d;
        max-width:108px;
        transition:background-color 0.15s ease, border-color 0.15s ease,
                   transform 0.08s ease, filter 0.1s ease;
    }
    .tshud-tile.lamp[data-lamp-state="red"] {
        background:#cc3521; border-color:#7f1d1d;
    }
    .tshud-tile.lamp[data-lamp-state="green"] {
        background:#358927; border-color:#1f5e1a;
    }
    .tshud-tile-label {
        font-size:11px; letter-spacing:1px;
        text-transform:uppercase;
        font-weight:900;
        line-height:1; text-align:center;
        color:#ffffff;
        text-shadow:0 1px 0 rgba(0,0,0,0.45);
    }

    /* Switch SVG illustration — compass hub. Shrunk so the
       tile can stay compact without leaving empty real estate
       below the artwork. */
    .tshud-switch-svg {
        width:54px; height:46px; display:block;
    }

    /* Barrier illustration — striped arm + signal light.
       Sized down to match the shorter lamp tile. */
    .tshud-barrier {
        width:60px; height:46px;
        position:relative;
        display:flex; align-items:flex-end; justify-content:center;
        gap:4px;
        padding-bottom:1px;
    }
    .tshud-barrier-pole {
        position:relative;
        width:7px; height:38px;
        background:linear-gradient(180deg,#cbd5e1 0%,#475569 100%);
        border:1px solid #0b1220; border-radius:2px;
        flex-shrink:0;
    }
    .tshud-barrier-arm {
        position:absolute; left:5px; top:2px;
        width:38px; height:7px;
        background:repeating-linear-gradient(
            90deg,
            #fefefe 0px, #fefefe 6px,
            #dc2626 6px, #dc2626 12px
        );
        border:1px solid #0b1220; border-radius:2px;
        transform-origin:left center;
        transform:rotate(0deg);
        transition:transform 0.25s ease;
        box-shadow:0 1px 2px rgba(0,0,0,0.3);
    }
    /* Real-crossing inversion: when the train signal is RED the
       track is safe, so the barrier is UP (cars pass). When the
       signal is GREEN the train is rolling, so the barrier is
       DOWN (cars stop). The HUD mirrors the on-board barrier so
       the player gets one consistent mental model in both places. */
    .tshud-barrier.red   .tshud-barrier-arm { transform:rotate(-78deg); }
    .tshud-barrier.green .tshud-barrier-arm { transform:rotate(0deg); }
    .tshud-barrier-light {
        width:13px; height:13px; border-radius:50%;
        background:#1f2937;
        border:2px solid #0b1220;
        box-shadow:inset 0 -2px 0 rgba(0,0,0,0.4);
        flex-shrink:0; align-self:center;
    }
    /* Barrier-mounted stop-light: flashes hot red when the barrier
       is DOWN (lamp green), goes dark when the barrier is UP. */
    .tshud-barrier.green .tshud-barrier-light {
        background:#ef4444;
        box-shadow:0 0 8px 2px rgba(239,68,68,0.95),
                   inset 0 -2px 0 rgba(0,0,0,0.4);
        animation:tshud-barrier-blink 0.85s ease-in-out infinite;
    }
    .tshud-barrier.red   .tshud-barrier-light {
        background:#f8fafc;
        box-shadow:0 0 6px 1px rgba(248,250,252,0.55),
                   inset 0 -2px 0 rgba(0,0,0,0.3);
    }
    @keyframes tshud-barrier-blink {
        0%, 100% { filter:brightness(0.65); }
        50%      { filter:brightness(1.25); }
    }

    /* Per-switch SIGNAL head shown on each LÁMPA tile: a two-aspect
       railway signal (red over green) in a dark housing. The lit
       aspect glows; the dark one is dimmed. The .red aspect strobes
       so a "stop" signal grabs the eye, matching the on-board head. */
    .tshud-signal {
        width:60px; height:46px;
        position:relative;
        display:flex; align-items:center; justify-content:center;
    }
    .tshud-signal-head {
        width:20px; height:38px; border-radius:8px;
        background:#0b1220; border:2px solid #000;
        display:flex; flex-direction:column; align-items:center;
        justify-content:space-around; padding:3px 0;
        box-shadow:inset 0 0 3px rgba(0,0,0,0.8);
    }
    .tshud-signal-bulb {
        width:13px; height:13px; border-radius:50%;
        background:#1c1c22; border:1px solid #000;
    }
    .tshud-signal.red .tshud-signal-bulb.red {
        background:#ef4444;
        box-shadow:0 0 8px 2px rgba(239,68,68,0.95);
        animation:tshud-signal-blink 0.7s ease-in-out infinite;
    }
    .tshud-signal.green .tshud-signal-bulb.green {
        background:#22c55e;
        box-shadow:0 0 8px 2px rgba(34,197,94,0.9);
    }
    @keyframes tshud-signal-blink {
        0%, 100% { filter:brightness(0.7); }
        50%      { filter:brightness(1.3); }
    }

    /* ── Legend ──────────────────────────────────────── */
    .tshud-legend {
        border-radius:10px; padding:5px 10px 6px;
        display:flex; flex-direction:column;
        box-shadow:0 3px 8px rgba(0,0,0,0.5),
                   inset 0 -3px 0 rgba(0,0,0,0.22);
        flex-shrink:0;
        background:#30215c; border:2px solid #f7bd3e;
        margin-left:auto; min-width:240px; max-width:320px;
    }
    .tshud-legend-title {
        font-size:11px; font-weight:900; letter-spacing:1.8px;
        margin-bottom:4px; text-align:center;
        color:#f7bd3e;
        text-shadow:0 1px 0 rgba(0,0,0,0.45);
    }
    .tshud-legend-cols {
        display:flex; gap:12px; align-items:flex-start;
        font-size:10px; font-weight:700;
    }
    .tshud-legend-col {
        display:flex; flex-direction:column; gap:2px; min-width:0; flex:1;
    }
    .tshud-legend-list {
        display:flex; flex-direction:column; gap:3px;
        overflow-y:auto; max-height:64px;
    }
    .tshud-legend-list::-webkit-scrollbar { width:4px; }
    .tshud-legend-list::-webkit-scrollbar-thumb { background:rgba(247,189,62,0.5); border-radius:2px; }
    .tshud-legend-row {
        display:flex; align-items:center; gap:6px;
        padding:1px 2px; border-radius:3px;
        white-space:nowrap;
        color:#ffffff; font-size:11px; font-weight:700;
    }
    .tshud-legend-row.arrived { opacity:0.55; }
    .tshud-legend-row.crashed { color:#fca5a5; text-decoration:line-through; }
    .tshud-legend-chip {
        display:inline-flex; align-items:center; justify-content:center;
        min-width:42px; padding:1px 5px;
        font-size:9px; font-weight:900; color:#fff;
        border-radius:4px;
        border:1px solid rgba(0,0,0,0.55);
        box-shadow:inset 0 -2px 0 rgba(0,0,0,0.30);
        flex-shrink:0;
    }
    .tshud-legend-dot {
        width:13px; height:13px; border-radius:50%;
        border:2px solid #0b1220; flex-shrink:0;
        box-shadow:inset 0 -2px 0 rgba(0,0,0,0.3);
    }
    .tshud-legend-dot.red   { background:#ef4444; box-shadow:0 0 6px rgba(239,68,68,0.7), inset 0 -2px 0 rgba(0,0,0,0.3); }
    .tshud-legend-dot.green { background:#22c55e; box-shadow:0 0 6px rgba(34,197,94,0.7), inset 0 -2px 0 rgba(0,0,0,0.3); }
    .tshud-legend-dot.gray  { background:#475569; }
    `;
    const style = document.createElement('style');
    style.dataset.tshud = '1';
    style.textContent = css;
    document.head.appendChild(style);
}

const COLOR_NAME_TO_CSS = {
    red:    '#ef4444',
    blue:   '#3b82f6',
    yellow: '#facc15',
    green:  '#22c55e',
    purple: '#a855f7',
};

const TRAIN_ID_TO_COLOR_NAME = {
    t1: 'red', t2: 'blue', t3: 'yellow', t4: 'green', t5: 'purple',
};

function trainColorCss(spawn) {
    const name = spawn?.props?.color
        || TRAIN_ID_TO_COLOR_NAME[spawn?.props?.trainId]
        || 'red';
    return COLOR_NAME_TO_CSS[name] || '#ffffff';
}

/**
/**
 * Pull a friendly station / train name from a spawn or goal entity.
 * Order of preference:
 *   1. The board's `_spawnDisplayCity` / `_goalDisplayCity` resolver
 *      (when a board is supplied) — this is the SAME logic that
 *      paints the "FROM X TO Y" info cards above each station and
 *      auto-detects a goal's destination by checking which other
 *      train's spawn footprint the goal pin sits inside. Using it
 *      here keeps the timetable's TO column perfectly in sync with
 *      the on-board card, so a designer who drags a goal onto
 *      "NYUGATI" instantly sees "TO Nyugati" in BOTH places without
 *      having to set any inspector field.
 *   2. Explicit `entity.props.stationName` (designer-set override).
 *   3. The skin label for `trainsim.spawn.{trainId}` / `.goal.{trainId}`
 *      ("Train 1 — Keleti") — we strip the "Train N —" prefix and
 *      any trailing "Goal" suffix to keep just the station name.
 *   4. Fallback: the trainId uppercased.
 *
 * @param {object} entity        spawn or goal entity
 * @param {object} themeManager  optional theme manager for slot labels
 * @param {'spawn'|'goal'} kind  which kind of station we want a name for
 * @param {object} [board]       optional TrainSimBoard for city resolvers
 */
function stationNameFor(entity, themeManager, kind, board = null) {
    if (!entity) return '—';

    // Prefer the board's resolver — it knows about destinationCity
    // overrides AND the auto-detect-by-footprint trick.
    if (board) {
        const resolver = kind === 'goal'
            ? board._goalDisplayCity?.bind(board)
            : board._spawnDisplayCity?.bind(board);
        const city = resolver ? resolver(entity) : '';
        if (city) return city;
    }

    const fromProp = entity.props?.stationName;
    if (fromProp) return fromProp;
    const trainId = entity.props?.trainId;
    if (trainId && themeManager?.getElement) {
        const slotKey = kind === 'goal'
            ? `trainsim.goal.${trainId}`
            : `trainsim.spawn.${trainId}`;
        const el = themeManager.getElement(slotKey);
        const lbl = el?.label || '';
        // "Train 1 — Keleti" → "Keleti"
        const cleaned = lbl.replace(/^Train\s+\d+\s*[—–-]\s*/i, '')
                           .replace(/\s*Goal.*$/i, '').trim();
        if (cleaned) return cleaned;
    }
    if (trainId) return trainId.toUpperCase();
    return '—';
}

export class TrainSimHud {
    /**
     * @param {object} opts
     * @param {import('./TrainSimBoard.js').TrainSimBoard} opts.board
     * @param {Array} opts.trains       live TrainSimTrain instances
     * @param {object} [opts.themeManager]
     */
    constructor(opts) {
        this._board = opts.board;
        this._trains = opts.trains || [];
        this._themeManager = opts.themeManager || null;
        this._onRestart = typeof opts.onRestart === 'function' ? opts.onRestart : null;
        // REWIND wiring. `onRewind` performs the actual rewind on
        // the scene side and returns true on success. The getters
        // let the HUD render an accurate "REWIND ×N" label and
        // disable the button when the player is out of uses.
        this._onRewind             = typeof opts.onRewind             === 'function' ? opts.onRewind             : null;
        this._getRewindsRemaining  = typeof opts.getRewindsRemaining  === 'function' ? opts.getRewindsRemaining  : null;
        this._getRewindMax         = typeof opts.getRewindMax         === 'function' ? opts.getRewindMax         : null;
        this._getSpeed   = typeof opts.getSpeed   === 'function' ? opts.getSpeed   : null;
        this._cycleSpeed = typeof opts.cycleSpeed === 'function' ? opts.cycleSpeed : null;
        // PAUSE + FOCUS tile wiring. `onPause` / `onFocus` flip the
        // scene-side flag and return the new state (true = ON / paused).
        // The getters (`getPaused`, `getFocused`) let `refresh()` mirror
        // the live state onto the tile in case anything else flips the
        // flag (e.g. SceneChrome auto-pausing on a modal). All four are
        // null when the corresponding Options toggle (`pauseButtonEnabled`
        // / `focusModeEnabled`) is off, in which case the bottom row
        // doesn't render the tile at all.
        this._onPause    = typeof opts.onPause    === 'function' ? opts.onPause    : null;
        this._getPaused  = typeof opts.getPaused  === 'function' ? opts.getPaused  : null;
        this._onFocus    = typeof opts.onFocus    === 'function' ? opts.onFocus    : null;
        this._getFocused = typeof opts.getFocused === 'function' ? opts.getFocused : null;
        // Live counters used by the top score / lives / time cards.
        // All optional — a HUD instance with none of them still
        // renders, just with placeholder zeroes / "—".
        this._getScore     = typeof opts.getScore     === 'function' ? opts.getScore     : null;
        this._getLives     = typeof opts.getLives     === 'function' ? opts.getLives     : null;
        this._getMaxLives  = typeof opts.getMaxLives  === 'function' ? opts.getMaxLives  : null;
        this._getClockText = typeof opts.getClockText === 'function' ? opts.getClockText : null;
        // Music / mute toggle (bottom-left card). Wired through to
        // whatever the scene wants — currently a stub `_muted` flag.
        this._getMuted     = typeof opts.getMuted     === 'function' ? opts.getMuted     : null;
        this._toggleMuted  = typeof opts.toggleMuted  === 'function' ? opts.toggleMuted  : null;
        // Help + leaderboard wiring for the small round buttons under
        // the IDŐ card. They replace the (now-hidden) Pixi chrome
        // buttons so the top-right corner matches the mockup exactly.
        this._onOpenHelp        = typeof opts.onOpenHelp        === 'function' ? opts.onOpenHelp        : null;
        this._onOpenLeaderboard = typeof opts.onOpenLeaderboard === 'function' ? opts.onOpenLeaderboard : null;
        this._onBack            = typeof opts.onBack            === 'function' ? opts.onBack            : null;
        // HU/EN language toggle for the round-button cluster. Null when the
        // deployment is pinned to a single locale (the button is skipped).
        this._onToggleLang      = typeof opts.onToggleLang      === 'function' ? opts.onToggleLang      : null;
        this._root = null;
        this._scheduleBody = null;
        this._switchBtns = new Map();
        // Per-switch LÁMPA (stop/go signal) tiles. One per switch,
        // matching the VÁLTÓ routing row count.
        this._signalBtns = new Map();
        this._scheduleRows = new Map();
        this._legendRows = new Map();
        // Persistent refs to the live counter elements so refresh()
        // can poke their text instead of rebuilding nodes per-frame.
        this._scoreValueEl = null;
        this._livesValueEl = null;
        this._livesPipsEl  = null;
        this._clockValueEl = null;
        // Big bottom-row action tiles, populated by their build
        // helpers. Kept on the instance so `_syncRewindTile` /
        // `_syncSpeedTile` (called every frame by refresh + on
        // demand by the scene) can update labels without a DOM
        // query.
        this._rewindTile        = null;
        this._rewindTileCountEl = null;
        this._speedTile         = null;
        this._speedTileValueEl  = null;
        // PAUSE + FOCUS tile element refs, populated by their builders
        // and consumed by `_syncPauseTile` / `_syncFocusTile` so every
        // frame's refresh updates label + class without DOM queries.
        this._pauseTile          = null;
        this._pauseTileLabelEl   = null;
        this._pauseTileIconEl    = null;
        this._focusTile          = null;
        this._focusTileValueEl   = null;
        this._onResize = () => this._applyScale();
    }

    mount() {
        injectStyles();
        const root = document.createElement('div');
        root.className = 'tshud-surface';

        // PONT (score) + ÉLET (lives) cards were intentionally removed
        // from the Train Simulator HUD per the latest design — the game
        // is a delivery puzzle, not a score chase, so the top-left
        // corner stays clean for the Pixi BACK button (positioned by
        // the scene) to live there without fighting for space.
        // `_syncStatCards` already null-guards the missing card refs,
        // so dropping the stack here is a safe one-line change.
        root.appendChild(this._buildClockCard());
        root.appendChild(this._buildTrButtons());
        root.appendChild(this._buildSchedule());
        root.appendChild(this._buildBottomRow());

        document.body.appendChild(root);
        this._root = root;
        this._applyScale();
        window.addEventListener('resize', this._onResize);
        this.refresh();
    }

    /**
     * Build the top-left PONT (score) + ÉLET (lives) cards, mounted
     * as a vertical stack that hugs the top-left corner. Both cards
     * use the cream + amber-border styling from the mockup.
     *
     * The lives card shows the number AND a heart-pip strip so the
     * remaining lives are readable at a glance even without
     * reading the digit (a single heart fading to grey is a more
     * immediate "you lost a life" signal than 3 → 2).
     */
    _buildStatStack() {
        const wrap = document.createElement('div');
        wrap.className = 'tshud-stat-stack';

        // PONT (score) card — yellow star + big navy number.
        const scoreCard = document.createElement('div');
        scoreCard.className = 'tshud-stat-card tshud-score';
        scoreCard.innerHTML = `
            <span class="tshud-stat-icon star">★</span>
            <span class="tshud-stat-body">
                <span class="tshud-stat-label">${(t('trainsim.score_label') || 'PONT').toUpperCase()}</span>
                <span class="tshud-stat-value" data-score>0</span>
            </span>
        `;
        this._scoreValueEl = scoreCard.querySelector('[data-score]');
        wrap.appendChild(scoreCard);

        // ÉLET (lives) card — red heart + big navy number.
        // (Mockup shows the digit only, no pip strip; the pip nodes
        // stay in the DOM but are hidden via CSS so the legacy
        // refresh path keeps working.)
        const livesCard = document.createElement('div');
        livesCard.className = 'tshud-stat-card tshud-lives';
        const maxLives = this._getMaxLives?.() ?? 3;
        livesCard.innerHTML = `
            <span class="tshud-stat-icon heart">♥</span>
            <span class="tshud-stat-body">
                <span class="tshud-stat-label">${(t('trainsim.lives_label') || 'ÉLET').toUpperCase()}</span>
                <span class="tshud-stat-value" data-lives>${maxLives}</span>
            </span>
        `;
        this._livesValueEl = livesCard.querySelector('[data-lives]');
        const pipsRow = document.createElement('div');
        pipsRow.className = 'tshud-life-pips';
        let pipsHtml = '';
        for (let i = 0; i < maxLives; i++) pipsHtml += '<span class="tshud-life-pip">♥</span>';
        pipsRow.innerHTML = pipsHtml;
        this._livesPipsEl = pipsRow;
        livesCard.querySelector('.tshud-stat-body').appendChild(pipsRow);
        wrap.appendChild(livesCard);

        return wrap;
    }

    /**
     * Top-right IDŐ (time) card — cream tile with thick navy
     * border, a yellow clock icon, and the running game clock.
     * Pinned to right:18; the smaller round help / leaderboard
     * buttons sit directly below it via _buildTrButtons().
     */
    _buildClockCard() {
        const card = document.createElement('div');
        card.className = 'tshud-clock-card';
        card.innerHTML = `
            <span class="tshud-clock-icon">⏰</span>
            <span class="tshud-clock-body">
                <span class="tshud-clock-label">${(t('trainsim.time_label') || 'IDŐ').toUpperCase()}</span>
                <span class="tshud-clock-value" data-clock>10:00</span>
            </span>
        `;
        this._clockValueEl = card.querySelector('[data-clock]');
        return card;
    }

    /**
     * Two small round buttons under the IDŐ card — orange
     * "stats" button (opens the leaderboard) and a gray "?"
     * button (opens the help overlay). They mirror the icons
     * in the mockup's top-right corner and replace the cyan
     * Pixi chrome buttons that are hidden in trainsim.
     */
    _buildTrButtons() {
        const wrap = document.createElement('div');
        wrap.className = 'tshud-tr-buttons';

        // Help button (gray ?)
        const helpBtn = document.createElement('button');
        helpBtn.type = 'button';
        helpBtn.className = 'tshud-tr-btn';
        helpBtn.innerHTML = '?';
        helpBtn.title = (t('trainsim.help_tip') || 'Help / how to play');
        helpBtn.addEventListener('click', (ev) => {
            ev.preventDefault(); ev.stopPropagation();
            this._onOpenHelp?.();
        });
        wrap.appendChild(helpBtn);

        // Leaderboard / stats button (orange bar-chart). Skipped
        // entirely when leaderboards are off — either because the
        // build was compiled without them (`__BUILD_LEADERBOARDS__`
        // false from data/shared.config.js) or because the runtime
        // toggle is off. The build-time flag short-circuits first
        // so the entire button-construction code is DCE'd from
        // bundles compiled with leaderboards disabled.
        const lbBuildEnabled = typeof globalThis.__BUILD_LEADERBOARDS__ !== 'undefined'
            ? globalThis.__BUILD_LEADERBOARDS__
            : true;
        const lbEnabled = lbBuildEnabled
            && this._themeManager?.getSetting?.('shared', 'leaderboardsEnabled', true) !== false;
        if (lbEnabled) {
            const lbBtn = document.createElement('button');
            lbBtn.type = 'button';
            lbBtn.className = 'tshud-tr-btn orange';
            // Bar-chart-style icon, drawn with three CSS-spaced bars.
            lbBtn.innerHTML = `
                <svg viewBox="0 0 24 24" width="22" height="22"
                     fill="none" stroke="currentColor" stroke-width="3"
                     stroke-linecap="round" stroke-linejoin="round">
                    <path d="M5 19V11"/><path d="M12 19V5"/><path d="M19 19V14"/>
                </svg>
            `;
            lbBtn.title = (t('trainsim.leaderboard_tip') || 'High scores');
            lbBtn.addEventListener('click', (ev) => {
                ev.preventDefault(); ev.stopPropagation();
                this._onOpenLeaderboard?.();
            });
            wrap.appendChild(lbBtn);
        }

        // Language toggle (HU/EN). Replaces the hidden canvas pill in
        // trainsim. Built only when the deployment allows switching —
        // same two-layer guard as the menu / SceneChrome:
        //   1. Build-time `__BUILD_LANG_MODE__` ('hu'/'en' DCEs it out).
        //   2. Runtime `availableLanguages` setting ('hu'/'en' hides it).
        const buildLangMode = typeof globalThis.__BUILD_LANG_MODE__ !== 'undefined'
            ? globalThis.__BUILD_LANG_MODE__
            : 'both';
        const langRestriction = buildLangMode !== 'both'
            ? buildLangMode
            : this._themeManager?.getSetting?.('shared.settings', 'availableLanguages', 'both');
        if (this._onToggleLang && langRestriction !== 'hu' && langRestriction !== 'en') {
            const langBtn = document.createElement('button');
            langBtn.type = 'button';
            langBtn.className = 'tshud-tr-btn';
            langBtn.textContent = otherLang().toUpperCase();
            langBtn.style.fontSize = '20px';
            langBtn.title = 'HU / EN';
            langBtn.addEventListener('click', (ev) => {
                ev.preventDefault(); ev.stopPropagation();
                this._onToggleLang?.();
            });
            wrap.appendChild(langBtn);
        }

        return wrap;
    }

    /** Mirror the canvas's letter-boxed scale so the HUD lives inside
     *  the visible 1920×1080 design area regardless of viewport size. */
    _applyScale() {
        if (!this._root) return;
        const fit = Math.min(
            window.innerWidth / DESIGN_W,
            window.innerHeight / DESIGN_H,
        );
        this._root.style.setProperty('--tshud-scale', String(fit));
    }

    // ── Schedule ───────────────────────────────────────────

    _buildSchedule() {
        const wrap = document.createElement('div');
        wrap.className = 'tshud-schedule';

        // Header bar: title centred, tool chips clustered at the edges.
        const header = document.createElement('div');
        header.className = 'tshud-schedule-header';
        const title = document.createElement('div');
        title.className = 'tshud-schedule-title';
        title.textContent = (t('trainsim.schedule_title') || 'MENETREND').toUpperCase();
        const leftCluster = document.createElement('div');
        leftCluster.className = 'tshud-schedule-left';
        if (this._onRestart) leftCluster.appendChild(this._buildRestartButton());
        header.appendChild(leftCluster);
        header.appendChild(title);

        const rightCluster = document.createElement('div');
        rightCluster.className = 'tshud-schedule-right';
        rightCluster.appendChild(this._buildRoutesToggle());
        header.appendChild(rightCluster);
        wrap.appendChild(header);

        // Two-column body. Trains are split roughly in half across
        // a LEFT and RIGHT mini-table — both with their own sticky
        // header — so the strip stays compact instead of growing
        // into a tall vertical list. With N trains, the left side
        // gets ceil(N/2), the right side gets floor(N/2). For odd
        // N the left column ends up with the extra row, which
        // reads naturally left-to-right like a real station board.
        const colHeaderHtml = `<tr>
            <th>${(t('trainsim.col_time')     || 'Idő').toUpperCase()}</th>
            <th>${(t('trainsim.col_from')     || 'Honnan').toUpperCase()}</th>
            <th>${(t('trainsim.col_to')       || 'Hova').toUpperCase()}</th>
            <th>${(t('trainsim.col_train')    || 'Vonat').toUpperCase()}</th>
            <th>${(t('trainsim.col_platform') || 'Vágány').toUpperCase()}</th>
            <th>${(t('trainsim.col_status')   || 'Állapot').toUpperCase()}</th>
        </tr>`;
        const colgroupHtml = `<colgroup>
            <col class="tshud-w-time"/>
            <col class="tshud-w-from"/>
            <col class="tshud-w-to"/>
            <col class="tshud-w-train"/>
            <col class="tshud-w-platform"/>
            <col class="tshud-w-status"/>
        </colgroup>`;

        const themeMgr = this._themeManager;
        // Mirror the previous global 1-based platform numbering so
        // VÁGÁNY values stay 1,2,3,4,5 across both columns instead
        // of restarting on the right side.
        let platformCounter = 0;

        const buildRowHtml = (train) => {
            const spawn = this._board.spawns.find((s) => s.props?.trainId === train.trainId);
            const goal  = this._board.goals.find((g) => g.props?.trainId === train.trainId);
            const delay = Number(spawn?.props?.startDelay ?? 0);
            const fromTxt = stationNameFor(spawn, themeMgr, 'spawn', this._board);
            const toTxt   = stationNameFor(goal,  themeMgr, 'goal',  this._board);
            const colorCss = trainColorCss(spawn);
            platformCounter += 1;
            const platform = Number(spawn?.props?.platform) || platformCounter;
            const routeLabel = this._routeLabel(toTxt, train.trainId);
            return `
                <td class="tshud-time">${this._formatTime(delay)}</td>
                <td><span class="tshud-station-name">${escapeHtml(fromTxt)}</span></td>
                <td><span class="tshud-station-name">${escapeHtml(toTxt)}</span></td>
                <td>
                    <span class="tshud-train-chip" style="background:${colorCss}">
                        ${escapeHtml(routeLabel)}
                    </span>
                </td>
                <td class="tshud-platform">${platform}</td>
                <td class="tshud-state">
                    <span class="tshud-status waiting">
                        ${(t('trainsim.status_waiting') || 'VÁR').toUpperCase()}
                    </span>
                </td>
            `;
        };

        const buildColumn = (trainsSubset) => {
            const col = document.createElement('div');
            col.className = 'tshud-schedule-col';
            const table = document.createElement('table');
            table.className = 'tshud-schedule-table';
            table.innerHTML = `${colgroupHtml}<thead>${colHeaderHtml}</thead>`;
            const tbody = document.createElement('tbody');
            for (const train of trainsSubset) {
                const tr = document.createElement('tr');
                tr.dataset.trainId = train.trainId;
                tr.innerHTML = buildRowHtml(train);
                tbody.appendChild(tr);
                this._scheduleRows.set(train.trainId, tr);
            }
            table.appendChild(tbody);
            const bodyWrap = document.createElement('div');
            bodyWrap.className = 'tshud-schedule-body-wrap';
            bodyWrap.appendChild(table);
            col.appendChild(bodyWrap);
            return { col, tbody };
        };

        const total = this._trains.length;
        const mid = Math.ceil(total / 2);
        const leftTrains  = this._trains.slice(0, mid);
        const rightTrains = this._trains.slice(mid);

        const columns = document.createElement('div');
        columns.className = 'tshud-schedule-columns';
        const left = buildColumn(leftTrains);
        columns.appendChild(left.col);
        if (rightTrains.length > 0) {
            const right = buildColumn(rightTrains);
            columns.appendChild(right.col);
        }
        wrap.appendChild(columns);
        // `_scheduleBody` is only used as a destroy-time fence so
        // keeping the left tbody is sufficient — every row lives
        // in `_scheduleRows`, which is what refresh() walks.
        this._scheduleBody = left.tbody;
        return wrap;
    }

    /**
     * Friendly route label for the VONAT column.
     *
     * "Debrecen" + "i" → "Debreceni"
     * "Szolnok" + "i"  → "Szolnoki"
     * "Szombathely" + "i" → "Szombathelyi"
     *
     * Falls back to the train id (in caps) for anything that doesn't
     * look like a Hungarian-style station name (so editor-set custom
     * labels don't get mangled).
     */
    _routeLabel(toName, trainId) {
        const clean = String(toName || '').trim();
        if (!clean) return String(trainId || '').toUpperCase();
        // Skip the suffix on labels that already end with "i" or
        // anything that doesn't look like a single proper noun.
        const single = clean.split(/[\s\-—]/)[0];
        if (!single) return clean;
        if (/i$/i.test(single)) return single;
        return single + 'i';
    }

    /**
     * Public helper: show a one-shot HUD toast with the given text.
     * `kind` is 'success' | 'warn' | 'error' (defaults to 'error'
     * since crash warnings are the main caller). Auto-dismisses
     * after ~5s. Used by the scene to surface crash / wrong-
     * destination messages without re-implementing the styling.
     */
    showToast(text, kind = 'error', durationMs = 5000) {
        if (!this._root || !text) return;
        if (this._toastEl) { this._toastEl.remove(); this._toastEl = null; }
        const toast = document.createElement('div');
        toast.className = `tshud-toast ${kind}`;
        toast.textContent = text;
        this._root.appendChild(toast);
        this._toastEl = toast;
        setTimeout(() => {
            if (toast.parentNode) toast.remove();
            if (this._toastEl === toast) this._toastEl = null;
        }, durationMs);
    }

    /**
     * Show the centred "dangerous traffic situation" banner. The
     * banner has NO auto-dismiss timer — the scene is responsible
     * for calling {@link hideDangerBanner} once the auto-rewind
     * lands. We also clear any existing toast so the player isn't
     * reading two competing messages at once.
     */
    showDangerBanner(text) {
        if (!this._root || !text) return;
        if (this._dangerBannerEl) { this._dangerBannerEl.remove(); this._dangerBannerEl = null; }
        if (this._toastEl) { this._toastEl.remove(); this._toastEl = null; }
        const el = document.createElement('div');
        el.className = 'tshud-danger-banner';
        el.textContent = text;
        this._root.appendChild(el);
        this._dangerBannerEl = el;
    }

    hideDangerBanner() {
        if (this._dangerBannerEl) {
            this._dangerBannerEl.remove();
            this._dangerBannerEl = null;
        }
    }

    _buildRestartButton() {
        const btn = document.createElement('button');
        btn.type = 'button';
        btn.className = 'tshud-restart-btn';
        btn.title = (t('trainsim.restart_tip') || 'Reset all trains to their starting positions and try again.');
        btn.innerHTML = `<span class="icon">↻</span><span class="lbl">${(t('trainsim.restart_btn') || 'RESTART').toUpperCase()}</span>`;
        btn.addEventListener('click', (ev) => {
            ev.preventDefault();
            ev.stopPropagation();
            this._onRestart?.();
        });
        return btn;
    }

    /**
     * Mirror the shared post-FX toggles (CRT, greyscale, old film) on
     * the HUD root so the DOM widgets — clock, schedule, switches,
     * lamps, legend — visually match whatever effect is running on
     * the Pixi canvas underneath them. Pure class toggling; the CSS
     * pseudo-element overlays defined at the top of this file do the
     * actual scanline / sepia / vignette work, so this method is
     * effectively free per frame and a single source of truth for
     * "is filter X active?".
     */
    _syncFilterClasses() {
        const getBool = (k) => !!this._themeManager?.getSetting?.('shared.settings', k, false);
        const flags = {
            'tshud-crt-on':       getBool('crtFilter'),
            'tshud-greyscale-on': getBool('greyscaleFilter'),
            'tshud-oldfilm-on':   getBool('oldFilmFilter'),
        };
        for (const [cls, on] of Object.entries(flags)) {
            if (this._root.classList.contains(cls) !== on) {
                this._root.classList.toggle(cls, on);
            }
        }
    }

    /**
     * Push the latest score / lives / clock values into the top
     * stat cards. Called every frame from `refresh()`. Each
     * element is updated in-place (text only) so we don't churn
     * the DOM — the values just tick.
     */
    _syncStatCards() {
        if (this._scoreValueEl && this._getScore) {
            const v = Math.max(0, Math.floor(this._getScore() || 0));
            const s = String(v);
            if (this._scoreValueEl.textContent !== s) this._scoreValueEl.textContent = s;
        }
        if (this._livesValueEl && this._getLives) {
            const v = Math.max(0, Math.floor(this._getLives() || 0));
            const s = String(v);
            if (this._livesValueEl.textContent !== s) this._livesValueEl.textContent = s;
            if (this._livesPipsEl) {
                const pips = this._livesPipsEl.querySelectorAll('.tshud-life-pip');
                pips.forEach((pip, i) => {
                    pip.classList.toggle('lost', i >= v);
                });
            }
        }
        if (this._clockValueEl && this._getClockText) {
            const txt = String(this._getClockText() || '—:—');
            if (this._clockValueEl.textContent !== txt) this._clockValueEl.textContent = txt;
        }
    }

    _buildRoutesToggle() {
        const btn = document.createElement('button');
        btn.type = 'button';
        btn.className = 'tshud-routes-toggle';
        // Sync visual state with whatever the board currently has.
        const startVisible = this._board._pathOverlayVisible !== false;
        if (!startVisible) btn.classList.add('off');
        btn.innerHTML = `<span class="dot"></span><span class="lbl">${(t('trainsim.routes_btn') || 'ROUTES').toUpperCase()}</span>`;
        btn.addEventListener('click', (ev) => {
            ev.preventDefault();
            ev.stopPropagation();
            const nowVisible = btn.classList.contains('off');
            btn.classList.toggle('off', !nowVisible);
            this._board.setPathOverlayVisible(nowVisible);
        });
        this._routesToggleBtn = btn;
        return btn;
    }

    // ── Bottom row (switches | actions | lamps) ────────────

    /**
     * Three-zone bottom row that mirrors the player's mental model:
     *
     *   ┌─ VÁLTÓ tiles ─────┐  ┌── action panel ──┐  ┌─ SOROMPÓ tiles ─┐
     *   │ switch  switch …  │  │ ⏸  ◎  ⏪  ⏩      │  │  lamp  lamp …   │
     *   └───────────────────┘  └──────────────────┘  └─────────────────┘
     *
     * Switches and lamps stretch into the side gutters; the action
     * group (PAUSE / FOCUS / REWIND / SPEED) is glass-panelled and
     * pinned in the centre so the player's eye lands on the global
     * controls first. The legacy JELMAGYARÁZAT (legend) panel was
     * removed per design — the schedule strip already carries the
     * route names, and the lamp colours are self-explanatory.
     *
     * Each action tile only renders when its corresponding scene
     * callback is wired (feature gated via Options) — a disabled
     * feature leaves its slot blank rather than shipping a dead
     * button, and the glass panel resizes to the active set.
     */
    _buildBottomRow() {
        const wrap = document.createElement('div');
        wrap.className = 'tshud-bottom-row';

        // ── Left gutter — VÁLTÓ (switches) ────────────────────
        const leftSide = document.createElement('div');
        leftSide.className = 'tshud-side-group tshud-side-left';
        const switches = this._collectFromBoard('switch');
        switches.forEach((sw, idx) => leftSide.appendChild(this._buildSwitchBtn(sw, idx + 1)));
        wrap.appendChild(leftSide);

        // ── Centre — global action panel ──────────────────────
        const actions = document.createElement('div');
        actions.className = 'tshud-actions-group';
        if (this._onPause)    actions.appendChild(this._buildPauseTile());
        if (this._onFocus)    actions.appendChild(this._buildFocusTile());
        if (this._onRewind)   actions.appendChild(this._buildRewindTile());
        if (this._cycleSpeed) actions.appendChild(this._buildSpeedTile());
        wrap.appendChild(actions);

        // ── Right gutter — LÁMPA (per-switch stop/go signals) ─
        // Barriers became automatic + car-only, so the old SOROMPÓ row
        // is repurposed into a LÁMPA row generated one-per-switch (so
        // its count matches the VÁLTÓ routing row). Each tile toggles
        // that switch's signal between red (stop) and green (go).
        const rightSide = document.createElement('div');
        rightSide.className = 'tshud-side-group tshud-side-right';
        switches.forEach((sw, idx) => rightSide.appendChild(this._buildSignalBtn(sw, idx + 1)));
        wrap.appendChild(rightSide);

        return wrap;
    }

    /**
     * Big "REWIND" tile that lives on the far left of the bottom
     * row. Shows an oversized ⏪ icon over the word REWIND and a
     * pill with the remaining uses count below. When the count
     * hits 0 the tile greys out, locks, and stops responding to
     * clicks.
     */
    _buildRewindTile() {
        const btn = document.createElement('button');
        btn.type = 'button';
        btn.className = 'tshud-action-tile rewind';
        btn.title = (t('trainsim.rewind_tip') || 'Rewind the whole game by 10 seconds — 5 uses per playthrough, 50 seconds total. Use it to undo a mistake.');
        btn.innerHTML = `
            <span class="tshud-action-label">${(t('trainsim.rewind_btn') || 'REWIND').toUpperCase()}</span>
            <span class="tshud-action-icon">⏪</span>
            <span class="tshud-action-value">×?</span>
        `;
        btn.addEventListener('click', (ev) => {
            ev.preventDefault();
            ev.stopPropagation();
            if (btn.classList.contains('disabled')) return;
            this._onRewind?.();
            // Sync immediately so the count drops to N-1 even
            // before the scene fires its own follow-up sync —
            // feels snappier.
            this._syncRewindTile();
        });
        this._rewindTile         = btn;
        this._rewindTileCountEl  = btn.querySelector('.tshud-action-value');
        this._syncRewindTile();
        return btn;
    }

    /**
     * Big "SPEED" tile next to REWIND. Click to cycle the train-
     * tempo multiplier through whatever steps the scene declared
     * (typically 1× / 2× / 4×). Background colour ramps from
     * yellow → orange → red as the multiplier grows so the player
     * gets a temperature cue for "trains are sped up".
     */
    _buildSpeedTile() {
        const btn = document.createElement('button');
        btn.type = 'button';
        btn.className = 'tshud-action-tile speed';
        btn.title = (t('trainsim.speed_tip') || 'Speed up the trains.');
        btn.innerHTML = `
            <span class="tshud-action-label">${(t('trainsim.speed_btn') || 'SPEED').toUpperCase()}</span>
            <span class="tshud-action-icon">⏩</span>
            <span class="tshud-action-value">1×</span>
        `;
        btn.addEventListener('click', (ev) => {
            ev.preventDefault();
            ev.stopPropagation();
            this._cycleSpeed?.();
            this._syncSpeedTile();
        });
        this._speedTile         = btn;
        this._speedTileValueEl  = btn.querySelector('.tshud-action-value');
        this._syncSpeedTile();
        return btn;
    }

    /**
     * Re-read the live rewind count from the scene callback and
     * mirror it onto the tile's value pill + disabled flag. Called
     * from `_buildRewindTile` (initial paint), from the click
     * handler, from the scene's `syncRewindButton` legacy hook,
     * and from `refresh()`.
     */
    _syncRewindTile() {
        if (!this._rewindTile) return;
        const left = typeof this._getRewindsRemaining === 'function'
            ? Math.max(0, Math.floor(this._getRewindsRemaining()))
            : 0;
        if (this._rewindTileCountEl) this._rewindTileCountEl.textContent = `×${left}`;
        this._rewindTile.classList.toggle('disabled', left <= 0);
        this._rewindTile.disabled = left <= 0;
    }

    /**
     * Back-compat alias — the scene still calls `syncRewindButton`
     * after a rewind animation completes. Keep it as a thin wrapper
     * around the new tile sync so existing callers don't need to
     * change.
     */
    syncRewindButton() {
        this._syncRewindTile();
    }

    /**
     * Re-read the live speed multiplier from the scene callback
     * and mirror it onto the tile's value pill + temperature
     * class. Cheap no-op when the speed tile isn't built (e.g.
     * the scene didn't wire `cycleSpeed`).
     */
    _syncSpeedTile() {
        if (!this._speedTile) return;
        const m = Number(this._getSpeed?.() ?? 1);
        if (this._speedTileValueEl) this._speedTileValueEl.textContent = `${m}×`;
        this._speedTile.classList.remove('s1', 's2', 'sN');
        // 1× → yellow, 2× → orange, anything ≥ 4× → red-hot.
        const cls = m <= 1 ? 's1' : m <= 2 ? 's2' : 'sN';
        this._speedTile.classList.add(cls);
    }

    /**
     * Big "PAUSE" tile next to focus / rewind. Click to freeze every
     * part of the sim (train movement, switch flips' grace timers,
     * road traffic, the in-world clock, snapshot history pushes).
     * Click again to resume. The visual flips between an emerald
     * ⏸ tile when running and an amber-pulsing ▶ tile when paused,
     * so the player can tell at a glance whether time is moving.
     */
    _buildPauseTile() {
        const btn = document.createElement('button');
        btn.type = 'button';
        btn.className = 'tshud-action-tile pause';
        btn.title = (t('trainsim.pause_tip') || 'Pause the simulator to think before you act.');
        btn.innerHTML = `
            <span class="tshud-action-label" data-pause-label>${(t('trainsim.pause_btn') || 'PAUSE').toUpperCase()}</span>
            <span class="tshud-action-icon" data-pause-icon>⏸</span>
            <span class="tshud-action-value" data-pause-state>—</span>
        `;
        btn.addEventListener('click', (ev) => {
            ev.preventDefault();
            ev.stopPropagation();
            this._onPause?.();
            // Sync immediately so the icon flips on click — even
            // before refresh() runs on the next frame.
            this._syncPauseTile();
        });
        this._pauseTile        = btn;
        this._pauseTileLabelEl = btn.querySelector('[data-pause-label]');
        this._pauseTileIconEl  = btn.querySelector('[data-pause-icon]');
        this._pauseTileStateEl = btn.querySelector('[data-pause-state]');
        this._syncPauseTile();
        return btn;
    }

    /**
     * Re-read the live pause flag from the scene callback and mirror
     * it onto the tile (label flips PAUSE↔RESUME, icon ⏸↔▶, class
     * `.paused` toggles the amber glow). Tolerant of a missing
     * `getPaused` — falls back to a "PAUSE" idle look.
     */
    _syncPauseTile() {
        if (!this._pauseTile) return;
        const paused = typeof this._getPaused === 'function'
            ? !!this._getPaused()
            : false;
        this._pauseTile.classList.toggle('paused', paused);
        if (this._pauseTileLabelEl) {
            this._pauseTileLabelEl.textContent = (paused
                ? (t('trainsim.resume_btn') || 'RESUME')
                : (t('trainsim.pause_btn')  || 'PAUSE')).toUpperCase();
        }
        if (this._pauseTileIconEl) {
            this._pauseTileIconEl.textContent = paused ? '▶' : '⏸';
        }
        if (this._pauseTileStateEl) {
            this._pauseTileStateEl.textContent = paused ? 'ON' : 'OFF';
        }
    }

    /**
     * Big "FOCUS" tile. Click to dim the background, road and
     * scenery so the gameplay layer (rails, route bands, switches,
     * lamps, trains) pops out of a darker scene. Click again to
     * restore. The tile carries an `.on` class while engaged so
     * the magenta glow signals the current mode at a glance.
     */
    _buildFocusTile() {
        const btn = document.createElement('button');
        btn.type = 'button';
        btn.className = 'tshud-action-tile focus';
        btn.title = (t('trainsim.focus_tip') || 'Dim the scenery so the trains and signals stand out.');
        btn.innerHTML = `
            <span class="tshud-action-label">${(t('trainsim.focus_btn') || 'FOCUS').toUpperCase()}</span>
            <span class="tshud-action-icon">◎</span>
            <span class="tshud-action-value" data-focus-state>OFF</span>
        `;
        btn.addEventListener('click', (ev) => {
            ev.preventDefault();
            ev.stopPropagation();
            this._onFocus?.();
            this._syncFocusTile();
        });
        this._focusTile        = btn;
        this._focusTileValueEl = btn.querySelector('[data-focus-state]');
        this._syncFocusTile();
        return btn;
    }

    /**
     * Re-read the live focus-mode flag from the scene callback and
     * mirror it onto the tile (state pill ON / OFF, plus the `.on`
     * class for the magenta glow). Tolerant of a missing
     * `getFocused` — defaults to the OFF look.
     */
    _syncFocusTile() {
        if (!this._focusTile) return;
        const on = typeof this._getFocused === 'function'
            ? !!this._getFocused()
            : false;
        this._focusTile.classList.toggle('on', on);
        if (this._focusTileValueEl) {
            this._focusTileValueEl.textContent = on ? 'ON' : 'OFF';
        }
    }

    /**
     * Legend on the right of the bottom row — two columns:
     *   • Trains: coloured route chip ("Debreceni" etc.) per train.
     *   • Lamps:  the three signal states (free / stop / off)
     *             so the player can decode the in-world signals.
     * Mirrors the JELMAGYARÁZAT panel in the mockup.
     */
    _buildLegend() {
        const legend = document.createElement('div');
        legend.className = 'tshud-legend';
        const legendTitle = document.createElement('div');
        legendTitle.className = 'tshud-legend-title';
        legendTitle.textContent = (t('trainsim.legend_label') || 'JELMAGYARÁZAT').toUpperCase();
        legend.appendChild(legendTitle);

        const cols = document.createElement('div');
        cols.className = 'tshud-legend-cols';

        // Trains column — coloured chip with the route name.
        const trainsCol = document.createElement('div');
        trainsCol.className = 'tshud-legend-col';
        const list = document.createElement('div');
        list.className = 'tshud-legend-list';
        for (const train of this._trains) {
            const spawn = this._board.spawns.find((s) => s.props?.trainId === train.trainId);
            const goal  = this._board.goals.find((g) => g.props?.trainId === train.trainId);
            const toTxt = stationNameFor(goal, this._themeManager, 'goal', this._board);
            const routeLabel = this._routeLabel(toTxt, train.trainId);
            const row = document.createElement('div');
            row.className = 'tshud-legend-row';
            row.dataset.trainId = train.trainId;
            row.innerHTML = `
                <span class="tshud-legend-chip" style="background:${trainColorCss(spawn)}">${escapeHtml(routeLabel)}</span>
            `;
            list.appendChild(row);
            this._legendRows.set(train.trainId, row);
        }
        trainsCol.appendChild(list);
        cols.appendChild(trainsCol);

        // Lamps column — three static state legends.
        const lampsCol = document.createElement('div');
        lampsCol.className = 'tshud-legend-col';
        const lampList = document.createElement('div');
        lampList.className = 'tshud-legend-list';
        lampList.innerHTML = `
            <div class="tshud-legend-row">
                <span class="tshud-legend-dot green"></span>
                <span>${escapeHtml(t('trainsim.lamp_free') || 'Szabad')}</span>
            </div>
            <div class="tshud-legend-row">
                <span class="tshud-legend-dot red"></span>
                <span>${escapeHtml(t('trainsim.lamp_stop') || 'Állj!')}</span>
            </div>
            <div class="tshud-legend-row">
                <span class="tshud-legend-dot gray"></span>
                <span>—</span>
            </div>
        `;
        lampsCol.appendChild(lampList);
        cols.appendChild(lampsCol);

        legend.appendChild(cols);
        return legend;
    }

    _collectFromBoard(type) {
        const out = [];
        for (const layer of (this._board._layers || [])) {
            for (const ent of (layer.entities || [])) {
                if (ent.type === type) out.push(ent);
            }
        }
        // Stable, intuitive ordering: top-to-bottom then left-to-right
        // matches reading order, so VÁLTÓ 1 is always the top-left switch.
        out.sort((a, b) => (a.r - b.r) || (a.c - b.c));
        return out;
    }

    _buildSwitchBtn(sw, ordinal) {
        const btn = document.createElement('button');
        btn.type = 'button';
        btn.className = 'tshud-tile switch';
        btn.dataset.entityId = sw.id;
        btn.innerHTML = `
            <span class="tshud-tile-label">${(t('trainsim.switch_short') || 'VÁLTÓ').toUpperCase()} ${ordinal}</span>
            ${this._switchSvg(sw)}
        `;
        btn.addEventListener('click', (ev) => {
            ev.preventDefault();
            ev.stopPropagation();
            this._board.toggleSwitch(sw);
            this._syncSwitchBtn(btn, sw);
        });
        // Strip the .flash class once the keyframe finishes so the
        // next toggle can re-trigger a fresh animation. Without this
        // the class would stay on the element forever and the
        // re-add in `_syncSwitchBtn` would have nothing to restart.
        btn.addEventListener('animationend', (ev) => {
            if (ev.animationName === 'tshud-switch-flash') {
                btn.classList.remove('flash');
            }
        });
        this._switchBtns.set(sw.id, btn);
        return btn;
    }

    /**
     * Render a "live junction" illustration: a compass-style hub
     * with one spoke for every open port of the underlying junction,
     * with the currently-connected pair(s) drawn bright white and
     * the other (preview) pairs dimmed. A small "n/N" badge in the
     * corner tells the player which routing they're on and how
     * many are available (1/3, 2/3, 3/3 for a T-junction, etc.).
     *
     * Falls back to the old straight + curve lever illustration when
     * the switch doesn't expose a routes list — protects against
     * legacy stages or programmatic switches that bypass the
     * derivation step.
     */
    _switchSvg(sw) {
        const routes = Array.isArray(sw._routes) ? sw._routes : [];
        const stateCount = routes.length;
        const railActive   = '#ffffff';
        const railInactive = 'rgba(255,255,255,0.35)';

        if (stateCount === 0) {
            // Legacy fallback: A/B lever.
            const isA = sw.active === 'A' || sw.active === 0;
            const colorStraight = isA ? railActive : railInactive;
            const colorCurve    = isA ? railInactive : railActive;
            const leverAngle = isA ? -22 : -68;
            return `
                <svg class="tshud-switch-svg" viewBox="0 0 72 60" xmlns="http://www.w3.org/2000/svg">
                    <g stroke="rgba(0,0,0,0.35)" stroke-width="2" stroke-linecap="round">
                        <line x1="10" y1="42" x2="10" y2="52"/>
                        <line x1="22" y1="42" x2="22" y2="52"/>
                        <line x1="34" y1="42" x2="34" y2="52"/>
                        <line x1="46" y1="42" x2="46" y2="52"/>
                        <line x1="58" y1="42" x2="58" y2="52"/>
                    </g>
                    <line stroke="${colorStraight}" stroke-width="4" stroke-linecap="round"
                          x1="4" y1="47" x2="68" y2="47"/>
                    <path stroke="${colorCurve}" stroke-width="4" stroke-linecap="round" fill="none"
                          d="M 4 47 Q 34 47 60 22"/>
                    <rect x="28" y="42" width="16" height="14" rx="2"
                          fill="#1f2937" stroke="#0b1220" stroke-width="1.5"/>
                    <circle cx="36" cy="42" r="2.5" fill="#f7bd3e" stroke="#0b1220" stroke-width="1"/>
                    <g transform="rotate(${leverAngle} 36 42)">
                        <line x1="36" y1="42" x2="36" y2="14" stroke="#0b1220" stroke-width="3" stroke-linecap="round"/>
                        <circle cx="36" cy="12" r="4" fill="#f7bd3e" stroke="#0b1220" stroke-width="1.5"/>
                    </g>
                </svg>
            `;
        }

        const activeIdx = Math.max(0, Math.min(stateCount - 1, Number(sw.active) || 0));
        const activeRoute = routes[activeIdx];

        // Pixi direction indices 0..7 → unit-circle angles. 0 = N
        // (up), advancing clockwise in 45° steps to match the
        // board's DIR_DELTA layout.
        const dirAngle = (d) => ((d - 2) * Math.PI) / 4; // 0=N→-π/2
        const opens = Array.isArray(sw._underlyingConn)
            ? sw._underlyingConn.map((v, i) => (v ? i : -1)).filter((i) => i >= 0)
            : [];

        // Collect only MUTUAL pairs (both a→b AND b→a in the
        // route) — those are the real through-lines. Extras
        // redirects (single-direction snaps like S→E for a T-
        // junction's orphan port) are NOT through-lines, and
        // including them would make every state of a T/Y
        // junction pick the SAME pair as primary so the mini
        // glyph would never visibly rotate. Mirrors the same
        // mutual-pair rule used by `_drawSwitchEntity`.
        const seen = new Set();
        const allActivePairs = [];
        if (activeRoute) {
            for (const [from, to] of activeRoute.entries()) {
                if (from < 0 || to < 0) continue;
                if (activeRoute.get(to) !== from) continue;
                const key = from < to ? `${from}-${to}` : `${to}-${from}`;
                if (seen.has(key)) continue;
                seen.add(key);
                allActivePairs.push([from, to]);
            }
        }
        // Canonical sort so the "primary" choice is STABLE across
        // toggles (Map iteration order isn't guaranteed to match
        // between equivalent states).
        allActivePairs.sort((p, q) => {
            const pMin = Math.min(p[0], p[1]);
            const qMin = Math.min(q[0], q[1]);
            if (pMin !== qMin) return pMin - qMin;
            return Math.max(p[0], p[1]) - Math.max(q[0], q[1]);
        });
        // Always highlight exactly ONE pair — the primary — so
        // the mini glyph reads as a single "active direction"
        // bar that visibly rotates each state. Same rule as the
        // on-board switch in `_drawSwitchEntity`.
        const activePairs = allActivePairs.slice(0, 1);

        // Single magenta colour matches the on-board switch — every
        // active pair paints in the same hot magenta so the HUD pill
        // and the board glyph read as the same "active direction"
        // signal. Rotation between states is communicated by
        // GEOMETRY (line vs corner arc), not by colour split.
        const ACTIVE_COLOR = '#ff2dd6';
        const colorForPair = () => ACTIVE_COLOR;

        // Layout: SVG hub at (36, 30), spokes reach radius 22.
        const cx = 36, cy = 30, R = 22;
        const ptFor = (d) => {
            const ang = dirAngle(d);
            return [cx + Math.cos(ang) * R, cy + Math.sin(ang) * R];
        };

        // Draw dim spokes for every open port first (so they read
        // as "available connections"), then overlay the active
        // pair(s) in colour on top.
        const dimSpokes = opens.map((d) => {
            const [x, y] = ptFor(d);
            return `<line x1="${cx}" y1="${cy}" x2="${x.toFixed(1)}" y2="${y.toFixed(1)}"
                          stroke="${railInactive}" stroke-width="3" stroke-linecap="round"/>`;
        }).join('');

        // Build the SVG `d` attribute for an active pair. Mirrors
        // the topology rules in `_drawSwitchEntity`:
        //   • opposite ports  → straight line through hub
        //   • perpendicular cardinals → quarter-arc around shared corner
        //   • everything else → bent line through hub
        const buildPairD = (a, b) => {
            const [x1, y1] = ptFor(a);
            const [x2, y2] = ptFor(b);
            const angularArc = Math.min(Math.abs(a - b), 8 - Math.abs(a - b));
            const isCardA = (a % 2 === 0);
            const isCardB = (b % 2 === 0);
            if (angularArc === 4) {
                return `M ${x1.toFixed(1)} ${y1.toFixed(1)} L ${x2.toFixed(1)} ${y2.toFixed(1)}`;
            }
            if (angularArc === 2 && isCardA && isCardB) {
                // Quarter-arc around the shared corner. SVG sweep
                // flag = 1 when the arc traces CW (a→b on the
                // compass) and 0 when it traces CCW. Pair direction
                // [a, b] vs [b, a] depends on iterator order, so
                // we compute the sweep from the SHORTER walk
                // around the compass — never the long way around.
                const cwSweep = (((b - a) + 8) % 8) === 2 ? 1 : 0;
                return `M ${x1.toFixed(1)} ${y1.toFixed(1)} A ${R.toFixed(1)} ${R.toFixed(1)} 0 0 ${cwSweep} ${x2.toFixed(1)} ${y2.toFixed(1)}`;
            }
            return `M ${x1.toFixed(1)} ${y1.toFixed(1)} L ${cx} ${cy} L ${x2.toFixed(1)} ${y2.toFixed(1)}`;
        };

        // Per-pair coloured strokes — pair A magenta, pair B cyan.
        const activeLines = activePairs.map(([a, b], idx) => {
            const stroke = colorForPair(idx);
            return `<path d="${buildPairD(a, b)}" fill="none"
                          stroke="${stroke}" stroke-width="4.5"
                          stroke-linecap="round" stroke-linejoin="round"/>`;
        }).join('');

        // Endpoint dots on every port — coloured to match the pair
        // they belong to. Lone (non-active) ports stay dim so the
        // active pair grouping reads at a glance.
        const portColorFor = (d) => {
            for (let i = 0; i < activePairs.length; i++) {
                if (activePairs[i].includes(d)) return colorForPair(i);
            }
            return null;
        };
        const ports = opens.map((d) => {
            const [x, y] = ptFor(d);
            const col = portColorFor(d);
            return `<circle cx="${x.toFixed(1)}" cy="${y.toFixed(1)}" r="3"
                            fill="${col || 'rgba(255,255,255,0.45)'}"
                            stroke="#0b1220" stroke-width="1"/>`;
        }).join('');

        // State counter — small pill in the upper-right.
        const counter = `
            <g>
                <rect x="50" y="2" width="20" height="12" rx="6"
                      fill="rgba(0,0,0,0.45)" stroke="rgba(255,255,255,0.55)" stroke-width="1"/>
                <text x="60" y="11" text-anchor="middle"
                      font-family="Arial, sans-serif" font-size="9" font-weight="800"
                      fill="#ffffff">${activeIdx + 1}/${stateCount}</text>
            </g>
        `;

        return `
            <svg class="tshud-switch-svg" viewBox="0 0 72 60" xmlns="http://www.w3.org/2000/svg">
                <!-- compass face -->
                <circle cx="${cx}" cy="${cy}" r="${R + 4}" fill="rgba(0,0,0,0.25)" stroke="rgba(255,255,255,0.25)" stroke-width="1"/>
                ${dimSpokes}
                ${activeLines}
                ${ports}
                <!-- hub -->
                <circle cx="${cx}" cy="${cy}" r="4" fill="#f7bd3e" stroke="#0b1220" stroke-width="1.5"/>
                ${counter}
            </svg>
        `;
    }

    /**
     * Build a LÁMPA tile for one switch — the per-switch stop/go
     * signal control. Clicking toggles the switch's `signal` between
     * red (stop) and green (go); the tile colour + signal head mirror
     * the live aspect. This is the new train-stop control (barriers
     * became automatic + car-only).
     */
    _buildSignalBtn(sw, ordinal) {
        const btn = document.createElement('button');
        btn.type = 'button';
        btn.className = 'tshud-tile lamp';
        btn.dataset.entityId = sw.id;
        // Seed the state attribute at construction so the CSS colour
        // matches the live aspect from the very first paint.
        btn.dataset.lampState = String(sw.signal || '');
        btn.innerHTML = `
            <span class="tshud-tile-label">${(t('trainsim.signal_short') || 'LÁMPA').toUpperCase()} ${ordinal}</span>
            ${this._signalHtml(sw)}
        `;
        btn.addEventListener('click', (ev) => {
            ev.preventDefault();
            ev.stopPropagation();
            this._board.toggleSwitchSignal(sw);
            this._syncSignalBtn(btn, sw);
            // Reflect the new aspect on the tile attribute immediately
            // so the colour swap is in lock-step with the click.
            btn.dataset.lampState = String(sw.signal || '');
        });
        this._signalBtns.set(sw.id, btn);
        return btn;
    }

    /**
     * Render a two-aspect railway signal head (red over green) for a
     * switch's stop/go signal. The lit aspect glows; the .red aspect
     * strobes — mirroring the on-board switch signal head.
     */
    _signalHtml(sw) {
        const cls = sw.signal === 'red'   ? 'red'
                  : sw.signal === 'green' ? 'green'
                  : '';
        return `
            <div class="tshud-signal ${cls}">
                <div class="tshud-signal-head">
                    <span class="tshud-signal-bulb red"></span>
                    <span class="tshud-signal-bulb green"></span>
                </div>
            </div>
        `;
    }

    _syncSwitchBtn(btn, sw) {
        const old = btn.querySelector('.tshud-switch-svg');
        if (!old) return;
        const tmp = document.createElement('div');
        tmp.innerHTML = this._switchSvg(sw).trim();
        const fresh = tmp.firstElementChild;
        old.replaceWith(fresh);
        // Pulse the tile to confirm "you just changed THIS switch".
        // Remove first + force reflow + re-add so the keyframe restarts
        // even when the same tile is toggled twice in a row — otherwise
        // the second click wouldn't visibly flash.
        btn.classList.remove('flash');
        void btn.offsetWidth;
        btn.classList.add('flash');
    }

    _syncSignalBtn(btn, sw) {
        const old = btn.querySelector('.tshud-signal');
        if (!old) return;
        const tmp = document.createElement('div');
        tmp.innerHTML = this._signalHtml(sw).trim();
        const fresh = tmp.firstElementChild;
        old.replaceWith(fresh);
    }

    // ── Live refresh (called every frame from the scene) ────

    refresh() {
        if (!this._root) return;
        this._syncFilterClasses();
        this._syncStatCards();
        // Keep the bottom REWIND tile + SPEED tile in sync with the
        // live scene-side state (count of rewinds left, current
        // tempo). Both methods are cheap no-ops when the matching
        // element isn't built (e.g. the scene didn't wire the
        // callback).
        this._syncRewindTile();
        this._syncSpeedTile();
        // PAUSE + FOCUS tiles also mirror live scene state each frame
        // so a programmatic flip (e.g. SceneChrome auto-pausing on a
        // modal, or another future input path toggling focus) shows
        // up on the tile without needing an explicit `syncPause()` /
        // `syncFocus()` push from the scene.
        this._syncPauseTile();
        this._syncFocusTile();

        // Find the train that departs NEXT — the still-waiting line
        // with the least time left — so we can pulse its ÁLLAPOT (time)
        // cell as the "up next" highlight. Ties resolve to the first
        // such train encountered.
        let nextUpId = null;
        let nextUpRemaining = Infinity;
        for (const train of this._trains) {
            if (train._started || !train._alive || train._reachedGoal) continue;
            const remaining = Math.max(0, (train.startDelay || 0) - (train._waitTimer || 0));
            if (remaining < nextUpRemaining) {
                nextUpRemaining = remaining;
                nextUpId = train.trainId;
            }
        }

        for (const train of this._trains) {
            const tr = this._scheduleRows.get(train.trainId);
            if (!tr) continue;
            const stateEl = tr.querySelector('.tshud-state .tshud-status');
            const { className, text } = this._trainStatus(train);
            stateEl.className = `tshud-status ${className}`;
            stateEl.textContent = text;

            // Pulse the ÁLLAPOT cell of the next-to-depart train only.
            const stateCell = stateEl.parentNode;
            if (stateCell?.classList) {
                stateCell.classList.toggle('next-up', train.trainId === nextUpId);
            }

            // Grey live countdown to departure, shown right after the
            // KÖVETKEZŐ / INDUL status while the train is still waiting
            // for its start delay. Disappears the instant it departs.
            let cd = tr.querySelector('.tshud-state .tshud-countdown');
            if (!train._started && train._alive && !train._reachedGoal) {
                const remaining = Math.max(0, (train.startDelay || 0) - (train._waitTimer || 0));
                if (!cd) {
                    cd = document.createElement('span');
                    cd.className = 'tshud-countdown';
                    stateEl.parentNode.appendChild(cd);
                }
                cd.textContent = this._formatCountdown(remaining);
                cd.style.display = '';
            } else if (cd) {
                cd.style.display = 'none';
            }

            tr.classList.remove('idle', 'running', 'arrived', 'crashed', 'boarding');
            tr.classList.add(
                className === 'arrived'  ? 'arrived'  :
                className === 'crashed'  ? 'crashed'  :
                className === 'boarding' ? 'boarding' :
                className === 'running' || className === 'departing' ? 'running' :
                'idle',
            );
            const legendRow = this._legendRows.get(train.trainId);
            if (legendRow) {
                legendRow.classList.toggle('arrived', className === 'arrived');
                legendRow.classList.toggle('crashed', className === 'crashed');
            }

            // Live reachability indicator — predicts the route from the
            // train's CURRENT position (or its spawn if it hasn't
            // started yet) under the live switch state. Once the train
            // has crossed a switch, the indicator only looks at the
            // remaining route ahead, so toggling a switch BEHIND the
            // train no longer falsely shows "blocked".
            const reachCell = tr.querySelector('.tshud-reach-cell .tshud-reach');
            if (reachCell) {
                let outcome;
                if (train._reachedGoal) outcome = 'reach';
                else if (!train._alive) outcome = 'crash';
                else outcome = this._board.predictTrainOutcome?.(train.trainId, train)?.outcome || 'crash';
                const { cls, icon, label } = this._reachChip(outcome);
                reachCell.className = `tshud-reach ${cls}`;
                reachCell.innerHTML = `<span class="tshud-reach-icon">${icon}</span><span class="tshud-reach-text">${label}</span>`;
            }
        }

        // Catch up button state if mutated externally (test-play
        // tooling, rewind animation, etc.). The SVG/HTML reflects
        // the live entity state — we cache the last-rendered state
        // on the button so we only re-render when it actually
        // changed.
        for (const [id, btn] of this._switchBtns) {
            const sw = this._findEntityById(id);
            if (!sw) continue;
            // `sw.active` is now an integer index (0..N-1); coerce
            // to a string for dataset compare so '0' / '1' / '2'
            // tracks cleanly without false positives from the
            // string ↔ number mismatch.
            const liveKey = String(sw.active ?? 0);
            if (btn.dataset.swState !== liveKey) {
                this._syncSwitchBtn(btn, sw);
                btn.dataset.swState = liveKey;
            }
        }
        for (const [id, btn] of this._signalBtns) {
            const sw = this._findEntityById(id);
            if (!sw) continue;
            const want = String(sw.signal || '');
            if (btn.dataset.lampState !== want) {
                this._syncSignalBtn(btn, sw);
                btn.dataset.lampState = want;
            }
        }
    }

    _findEntityById(id) {
        for (const layer of (this._board._layers || [])) {
            for (const ent of (layer.entities || [])) {
                if (ent.id === id) return ent;
            }
        }
        return null;
    }

    _reachChip(outcome) {
        if (outcome === 'reach')      return { cls: 'ok',    icon: '✓', label: (t('trainsim.reach_ok')    || 'OK').toUpperCase() };
        if (outcome === 'loop')       return { cls: 'loop',  icon: '∞', label: (t('trainsim.reach_loop')  || 'LOOP').toUpperCase() };
        if (outcome === 'wrong-goal') return { cls: 'crash', icon: '!', label: (t('trainsim.reach_wrong') || 'WRONG DEST!').toUpperCase() };
        return                               { cls: 'crash', icon: '✕', label: (t('trainsim.reach_crash') || 'BLOCKED').toUpperCase() };
    }

    /**
     * Format a remaining-seconds value as a compact `M:SS` countdown
     * (e.g. 42 → "0:42", 75 → "1:15"). Always rounds UP so the clock
     * shows "0:01" for the final fractional second instead of snapping
     * to "0:00" while the train is still waiting.
     */
    _formatCountdown(sec) {
        const s = Math.max(0, Math.ceil(sec));
        const m = Math.floor(s / 60);
        const r = s % 60;
        return `${m}:${String(r).padStart(2, '0')}`;
    }

    _trainStatus(train) {
        if (!train._alive)        return { className: 'crashed',   text: (t('trainsim.status_crashed')  || 'CRASH').toUpperCase() };
        if (train._reachedGoal)   return { className: 'arrived',   text: (t('trainsim.status_arrived')  || 'ARRIVED').toUpperCase() };
        if (!train._started) {
            // Final-10s pre-departure window: the scene's announcer
            // already plays the bell once when this flag flips; HUD
            // just needs to surface the matching visual cue (green
            // pulsing INDUL/BOARDING text + flashing row class) so
            // the player can see WHICH row is next.
            if (train._isBoarding) return { className: 'boarding',  text: (t('trainsim.status_boarding') || 'INDUL').toUpperCase() };
            return                       { className: 'next',      text: (t('trainsim.status_waiting')  || 'WAIT').toUpperCase() };
        }
        if (train.paused)         return { className: 'waiting',   text: (t('trainsim.status_holding')  || 'HOLD').toUpperCase() };
        return { className: 'running', text: (t('trainsim.status_running') || 'RUN').toUpperCase() };
    }

    /**
     * Show schedule times as the in-world wall-clock departure
     * (10:00, 10:02, 10:05…) rather than relative delays, matching
     * the mockup. Conversion mirrors TrainSimScene's clock
     * constants: the in-world day starts at 10:00 and every 5 real
     * seconds == 1 in-world minute.
     */
    _formatTime(seconds) {
        const CLOCK_START_MIN = 600;
        const CLOCK_RATE      = 5;
        const delay = Math.max(0, Number(seconds) || 0);
        const totalMin = CLOCK_START_MIN + Math.floor(delay / CLOCK_RATE);
        const hh = Math.floor(totalMin / 60) % 24;
        const mm = totalMin % 60;
        return `${String(hh).padStart(2, '0')}:${String(mm).padStart(2, '0')}`;
    }

    destroy() {
        window.removeEventListener('resize', this._onResize);
        if (this._toastEl) { this._toastEl.remove(); this._toastEl = null; }
        if (this._dangerBannerEl) { this._dangerBannerEl.remove(); this._dangerBannerEl = null; }
        if (this._root) this._root.remove();
        this._root = null;
        this._scheduleBody = null;
        this._switchBtns.clear();
        this._signalBtns.clear();
        this._scheduleRows.clear();
        this._legendRows.clear();
        this._rewindTile = null;
        this._rewindTileCountEl = null;
        this._speedTile = null;
        this._speedTileValueEl = null;
    }
}

function escapeHtml(s) {
    return String(s ?? '')
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(/"/g, '&quot;');
}
